import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Stage,
    Layer,
    Rect,
    Text,
    Group,
    Circle,
} from 'react-konva';
import { apiClient } from '../services/api';
import { decodeUrlForDisplay } from '../utils/urlDisplay';
import type { ViolationData, ViolationFixability } from '../types/violation';
import FixabilityEvidenceBlock from './violation/FixabilityEvidenceBlock';
import ViolationFixabilityControls from './violation/ViolationFixabilityControls';
import { useToast } from './toast/ToastProvider';
import {
    countFixabilityBreakdown,
    findMatchingMediaRuleExcerpt,
    isUnfixableViolation,
    readShowUnfixablePreference,
    writeShowUnfixablePreference,
    UNFIXABLE_BOX_FILL,
    UNFIXABLE_BOX_STROKE,
} from '../utils/violationFixability';

interface ViolationCanvasProps {
    violations?: ViolationData[];
    imageUrl?: string;
    jobId?: string;
    urlId?: string;
    /** If provided, selects (and scrolls to) this violation on load. */
    initialSelectedViolationId?: string;
    /** View-only mode: disable edits (draw/resize/delete/edit). */
    readOnly?: boolean;
    /** If true, include deleted violations in the canvas/list visibility. */
    includeDeleted?: boolean;
    onViolationsChange?: (violations: ViolationData[], deletedIds: string[]) => void;
    categoryContentExistenceDict?: Record<string, boolean>;
    onCategoryContentExistenceDictChange?: (dict: Record<string, boolean>) => void;
    violationCategories?: Array<{ id: string; title: string }>;
    onSave?: () => void | Promise<void>;
    onMarkReviewed?: () => void | Promise<void>;
    saveLoading?: boolean;
    reviewLoading?: boolean;
    errorText?: string | null;
    /** Display name for the job (sidebar header). */
    jobName?: string;
    /** Public URL of the page being reviewed; opens in a new tab when clicked. */
    pageUrl?: string;
    /** Where "ジョブのURL一覧へ" navigates (preserves filters/page when set from results list). */
    resultsListHref?: string;
    /** Guideline 修正不可内容 for this page domain (from GET violations, not per violation). */
    pageUnfixableRuleDescription?: string;
    /** Guideline version has 修正可否自動分類 enabled (from GET violations). */
    enableUnfixableClassification?: boolean;
}

// Helper functions to convert between ratios and pixels
const ratioToPixels = (ratio: number, dimension: number) => ratio * dimension;
const pixelsToRatio = (pixels: number, dimension: number) => pixels / dimension;

const SIDEBAR_WIDTH_PX = 320;
const SIDEBAR_VISIBILITY_STORAGE_KEY = 'violationCanvas.sidebarVisible';
const DEFAULT_BOX_STROKE = '#00ffff';
const DEFAULT_BOX_FILL = 'rgba(0, 255, 255, 0.3)';
const DEFAULT_BOX_STROKE_WIDTH = 2;
const SELECTED_BOX_STROKE_WIDTH = 4;

// Shared sizing for the edit-note text in the violation list so the box caps out
// at the same height whether it's showing plain text or the editable textarea.
const VIOLATION_NOTE_FONT_SIZE_PX = 14;
const VIOLATION_NOTE_LINE_HEIGHT = 1.4;
const VIOLATION_NOTE_MAX_LINES = 6;
const VIOLATION_NOTE_MAX_HEIGHT_PX = Math.ceil(
    VIOLATION_NOTE_FONT_SIZE_PX * VIOLATION_NOTE_LINE_HEIGHT * VIOLATION_NOTE_MAX_LINES
);

const ViolationCanvas: React.FC<ViolationCanvasProps> = ({
    violations = [],
    imageUrl,
    jobId,
    urlId,
    readOnly = false,
    includeDeleted = false,
    onViolationsChange,
    categoryContentExistenceDict = {},
    onCategoryContentExistenceDictChange,
    violationCategories = [],
    onSave,
    onMarkReviewed,
    saveLoading = false,
    reviewLoading = false,
    errorText = null,
    jobName,
    pageUrl,
    resultsListHref,
    pageUnfixableRuleDescription,
    enableUnfixableClassification = false,
    initialSelectedViolationId,
}) => {
    const toast = useToast();
    const isReadOnly = Boolean(readOnly);
    const allowDeletedInView = Boolean(includeDeleted);
    // Use categories from guideline (no hardcoded fallback)
    const VIOLATION_CATEGORIES = violationCategories;

    // Helper function to get category title from ID
    const getCategoryTitle = (categoryId: string): string => {
        const category = VIOLATION_CATEGORIES.find(cat => cat.id === categoryId);
        return category ? category.title : categoryId;
    };

    const [annotations, setAnnotations] = useState<ViolationData[]>([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
    const [currentAnnotation, setCurrentAnnotation] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
    const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
    const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editCaptureUrl, setEditCaptureUrl] = useState('');
    const [captureUrlPreviewError, setCaptureUrlPreviewError] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState<string | null>(null);
    const [resizeStartPoint, setResizeStartPoint] = useState<{ x: number; y: number } | null>(null);
    const [resizeStartAnnotation, setResizeStartAnnotation] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [imageLoading, setImageLoading] = useState<boolean>(true);
    const [imageError, setImageError] = useState<string | null>(null);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const [imageRendered, setImageRendered] = useState<boolean>(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const [showShortcutsTooltip, setShowShortcutsTooltip] = useState<boolean>(false);
    const [showUnfixableRuleTooltip, setShowUnfixableRuleTooltip] = useState<boolean>(false);
    const [zoomPanelEnabled, setZoomPanelEnabled] = useState<boolean>(true);
    const [labelsAboveBoxesEnabled, setLabelsAboveBoxesEnabled] = useState<boolean>(false);
    const [sidebarVisible, setSidebarVisible] = useState<boolean>(() => {
        try {
            const stored = localStorage.getItem(SIDEBAR_VISIBILITY_STORAGE_KEY);
            const resolved = stored === null ? false : stored === 'true';
            console.log('[ViolationCanvas] drawer init', {
                storageKey: SIDEBAR_VISIBILITY_STORAGE_KEY,
                storedValue: stored,
                resolvedVisible: resolved,
            });
            return resolved;
        } catch {
            console.log('[ViolationCanvas] drawer init: localStorage read failed; defaulting to hidden');
            return false;
        }
    });
    const [drawerTransitionsEnabled, setDrawerTransitionsEnabled] = useState(false);
    const [drawerHandleHover, setDrawerHandleHover] = useState(false);
    const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null);
    const [showZoomedView, setShowZoomedView] = useState<boolean>(false);
    const [zoomedViewPosition, setZoomedViewPosition] = useState<{ top: number; left: number; width: number } | null>(null);
    const [isCategoryContentExistenceExpanded, setIsCategoryContentExistenceExpanded] = useState<boolean>(true);
    const [showUnfixableViolations, setShowUnfixableViolations] = useState<boolean>(() =>
        readShowUnfixablePreference(jobId, urlId)
    );
    const zoomCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const lastZoomPanelRef = useRef<{ id: string; top: number; left: number; width: number } | null>(null);
    const processingAnnotationIdRef = useRef<string | null>(null);
    const selectedAnnotationIdRef = useRef<string | null>(null);
    const annotationsRef = useRef<ViolationData[]>(annotations);
    const pendingScrollTimeoutRef = useRef<number | null>(null);
    const pendingScrollUpdateTimeoutRef = useRef<number | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const stageContainerRef = useRef<HTMLDivElement | null>(null);
    const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
    const imageBlobUrlRef = useRef<string | null>(null); // Track blob URL for cleanup

    const sidebarWidthPx = sidebarVisible ? SIDEBAR_WIDTH_PX : 0;

    const toggleSidebar = useCallback(() => {
        setSidebarVisible((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_VISIBILITY_STORAGE_KEY, String(next));
                console.log('[ViolationCanvas] drawer toggle', {
                    storageKey: SIDEBAR_VISIBILITY_STORAGE_KEY,
                    prevVisible: prev,
                    nextVisible: next,
                    wroteValue: String(next),
                });
            } catch {
                console.log('[ViolationCanvas] drawer toggle: localStorage write failed', {
                    prevVisible: prev,
                    nextVisible: next,
                });
                // ignore
            }
            return next;
        });
    }, []);

    useEffect(() => {
        // Avoid initial "layout flash" on first page load by enabling
        // width/transform transitions only after the first paint.
        setDrawerTransitionsEnabled(true);
    }, []);

    useEffect(() => {
        console.log('[ViolationCanvas] drawer state', {
            visible: sidebarVisible,
            sidebarWidthPx,
            transitionsEnabled: drawerTransitionsEnabled,
        });
    }, [sidebarVisible, sidebarWidthPx, drawerTransitionsEnabled]);

    useEffect(() => {
        setShowUnfixableViolations(readShowUnfixablePreference(jobId, urlId));
    }, [jobId, urlId]);

    const passesUnfixableVisibility = useCallback(
        (v: ViolationData) =>
            !enableUnfixableClassification
            || showUnfixableViolations
            || !isUnfixableViolation(v),
        [enableUnfixableClassification, showUnfixableViolations]
    );

    const passesCategoryFilter = useCallback(
        (v: ViolationData) => !categoryFilter || v.category === categoryFilter,
        [categoryFilter]
    );

    /** Sidebar + canvas visibility (category, unfixable); optionally includes deleted. */
    const passesCanvasVisibility = useCallback(
        (v: ViolationData) =>
            (allowDeletedInView || !v.deleted)
            && passesUnfixableVisibility(v)
            && passesCategoryFilter(v),
        [allowDeletedInView, passesUnfixableVisibility, passesCategoryFilter]
    );

    useEffect(() => {
        if (!selectedAnnotationId) return;
        const selected = annotations.find((a) => a.id === selectedAnnotationId);
        if (!selected || !passesCanvasVisibility(selected)) {
            console.log('[ViolationCanvas] clearing selection: not visible', {
                selectedAnnotationId,
                found: Boolean(selected),
                allowDeletedInView,
                deleted: Boolean(selected?.deleted),
            });
            setSelectedAnnotationId(null);
        }
    }, [selectedAnnotationId, annotations, passesCanvasVisibility]);

    const sortedVisibleAnnotations = useMemo(() => {
        const visibleAnnotations = annotations.filter(passesCanvasVisibility);
        if (visibleAnnotations.length === 0) return [];

        const hasBoxPosition = (v: ViolationData): boolean => {
            const xr = typeof v.xRatio === 'number' ? v.xRatio : undefined;
            const yr = typeof v.yRatio === 'number' ? v.yRatio : undefined;
            const wr = typeof v.widthRatio === 'number' ? v.widthRatio : undefined;
            const hr = typeof v.heightRatio === 'number' ? v.heightRatio : undefined;
            return !!(xr !== undefined && yr !== undefined && wr !== undefined && hr !== undefined && wr > 0 && hr > 0);
        };

        // Match the same ordering as keyboard navigation: unpositioned first, then top-to-bottom / left-to-right.
        return [...visibleAnnotations].sort((a, b) => {
            const aHasPosition = hasBoxPosition(a);
            const bHasPosition = hasBoxPosition(b);

            if (!aHasPosition && !bHasPosition) {
                // overall-page first, then off-capture, preserving original order
                const aOff = a.scope === 'off_capture' ? 1 : 0;
                const bOff = b.scope === 'off_capture' ? 1 : 0;
                if (aOff !== bOff) return aOff - bOff;
                return 0;
            }
            if (!aHasPosition) return -1;
            if (!bHasPosition) return 1;

            const aCenterY = ratioToPixels((a.yRatio || 0) + (a.heightRatio || 0) / 2, imageDimensions.height);
            const bCenterY = ratioToPixels((b.yRatio || 0) + (b.heightRatio || 0) / 2, imageDimensions.height);

            const yDiff = aCenterY - bCenterY;
            if (Math.abs(yDiff) > 5) {
                return yDiff;
            }

            const aCenterX = ratioToPixels((a.xRatio || 0) + (a.widthRatio || 0) / 2, imageDimensions.width);
            const bCenterX = ratioToPixels((b.xRatio || 0) + (b.widthRatio || 0) / 2, imageDimensions.width);
            return aCenterX - bCenterX;
        });
    }, [annotations, passesCanvasVisibility, imageDimensions]);

    const didAutoSelectRef = useRef(false);
    /** Apply URL `violationId` only once; do not override user clicks when annotations update. */
    const appliedInitialViolationIdRef = useRef<string | null>(null);

    useEffect(() => {
        didAutoSelectRef.current = false;
        appliedInitialViolationIdRef.current = null;
    }, [jobId, urlId]);

    const isAnnotationUnfixable = useCallback(
        (v: ViolationData) => enableUnfixableClassification && isUnfixableViolation(v),
        [enableUnfixableClassification]
    );

    const fixabilityBreakdown = useMemo(
        () => countFixabilityBreakdown(annotations),
        [annotations]
    );

    const handleShowUnfixableToggle = useCallback((checked: boolean) => {
        setShowUnfixableViolations(checked);
        writeShowUnfixablePreference(jobId, urlId, checked);
    }, [jobId, urlId]);

    useEffect(() => {
        annotationsRef.current = annotations;
    }, [annotations]);

    useEffect(() => {
        selectedAnnotationIdRef.current = selectedAnnotationId;
    }, [selectedAnnotationId]);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (imageBlobUrlRef.current) {
                URL.revokeObjectURL(imageBlobUrlRef.current);
                imageBlobUrlRef.current = null;
            }
            if (pendingScrollTimeoutRef.current !== null) {
                clearTimeout(pendingScrollTimeoutRef.current);
            }
            if (pendingScrollUpdateTimeoutRef.current !== null) {
                clearTimeout(pendingScrollUpdateTimeoutRef.current);
            }
        };
    }, []);

    // Debug flag to enable/disable export button
    const ENABLE_EXPORT_BUTTON = false; // Set to true to enable export button for debugging

    // Determine which annotations have positions
    const hasPosition = (v: ViolationData): boolean => {
        const xr = typeof v.xRatio === 'number' ? v.xRatio : undefined;
        const yr = typeof v.yRatio === 'number' ? v.yRatio : undefined;
        const wr = typeof v.widthRatio === 'number' ? v.widthRatio : undefined;
        const hr = typeof v.heightRatio === 'number' ? v.heightRatio : undefined;
        return !!(xr !== undefined && yr !== undefined && wr !== undefined && hr !== undefined && wr > 0 && hr > 0);
    };

    // Determine "overall page" violations by scope (not by missing coordinates).
    const isOverallPageScope = (v: ViolationData): boolean => v.scope === 'overall_page';

    // Violations that are not present in the capture because full-page capture
    // failed. They have no position on the image; reviewers add them manually
    // with both violation content and edit note.
    const isOffCaptureScope = (v: ViolationData): boolean => v.scope === 'off_capture';

    // Title shown for a header-band row. Off-capture rows are prefixed so they are
    // visually distinguishable from overall-page ones (used for both measuring and
    // rendering so the layout stays consistent).
    const getHeaderRowTitle = (annotation: ViolationData): string => {
        const base = annotation.editNote || annotation.violationContent || '(テキストなし)';
        return isOffCaptureScope(annotation) ? `📷【キャプチャ外】${base}` : base;
    };

    // Open URLs embedded in off-capture violation content (reviewers usually paste
    // the manually captured page URL there).
    const openUrlInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Hidden canvas context for measuring text width for wrapping
    const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const getMeasureContext = (): CanvasRenderingContext2D | null => {
        if (!measureCanvasRef.current) {
            measureCanvasRef.current = document.createElement('canvas');
        }
        const ctx = measureCanvasRef.current.getContext('2d');
        return ctx;
    };

    const hasPageUnfixableRule = Boolean(pageUnfixableRuleDescription?.trim());

    const SHORTCUTS_TOOLTIP = [
        'ショートカット一覧',
        '',
        'Tab — 次の違反に移動',
        'Enter — 選択中の違反を編集',
        'Backspace — 選択中の違反を削除',
        'Ctrl/Cmd + Z — 直前に削除した違反を復元',
        '',
        '編集ポップアップ表示中',
        'Shift + Enter — 保存',
        'Escape — キャンセル',
        'Enter — 改行を挿入'
    ].join('\n');

    // Geometry helpers for overlap detection
    const rectsOverlap = (
        a: { x: number; y: number; width: number; height: number },
        b: { x: number; y: number; width: number; height: number }
    ): boolean => {
        return !(
            a.x + a.width <= b.x ||
            b.x + b.width <= a.x ||
            a.y + a.height <= b.y ||
            b.y + b.height <= a.y
        );
    };

    const computeLabelLayout = (
        annotation: ViolationData,
        imageW: number,
        imageH: number,
        fontSize = 14
    ): {
        lines: string[];
        bgWidth: number;
        bgHeight: number;
        textY: number;
        padding: number;
        maxTextWidth: number;
    } => {
        const groupX = ratioToPixels(annotation.xRatio || 0, imageW);
        const rectWidthPx = ratioToPixels(annotation.widthRatio || 0, imageW);
        const availableWidth = Math.max(0, imageW - groupX - 10);
        const preferredWidth = Math.max(rectWidthPx * 3, 220);
        const maxTextWidth = Math.max(0, Math.min(preferredWidth, availableWidth));
        const padding = 6;
        let wrap = insertSingleNewlineIfOverflow(annotation.editNote || '', maxTextWidth, fontSize, 'sans-serif');
        if (wrap.maxLineWidth > maxTextWidth) {
            wrap = wrapTextToWidth(annotation.editNote || '', maxTextWidth, fontSize, 'sans-serif');
        }
        const bgWidth = Math.ceil(Math.min(wrap.maxLineWidth, maxTextWidth)) + padding * 2;
        const bgHeight = wrap.totalHeight + padding * 2;
        const textY = -bgHeight - 4;
        return { lines: wrap.lines, bgWidth, bgHeight, textY, padding, maxTextWidth };
    };

    const breakLongWordByChars = (
        word: string,
        maxWidth: number,
        fontSize: number,
        fontFamily: string
    ): string[] => {
        const ctx = getMeasureContext();
        if (!ctx || maxWidth <= 0) return [word];
        ctx.font = `${fontSize}px ${fontFamily}`;
        const parts: string[] = [];
        let current = '';
        for (let i = 0; i < word.length; i++) {
            const test = current + word[i];
            if (ctx.measureText(test).width <= maxWidth || current.length === 0) {
                current = test;
            } else {
                parts.push(current);
                current = word[i];
            }
        }
        if (current) parts.push(current);
        return parts;
    };

    const wrapTextToWidth = (
        text: string,
        maxWidth: number,
        fontSize: number,
        fontFamily = 'sans-serif'
    ): { lines: string[]; maxLineWidth: number; lineHeight: number; totalHeight: number } => {
        const ctx = getMeasureContext();
        if (!ctx || !text) {
            const lineHeight = Math.round(fontSize * 1.2);
            return { lines: [''], maxLineWidth: 0, lineHeight, totalHeight: lineHeight };
        }
        ctx.font = `${fontSize}px ${fontFamily}`;
        const words = String(text).trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            const lineHeight = Math.round(fontSize * 1.2);
            return { lines: [''], maxLineWidth: 0, lineHeight, totalHeight: lineHeight };
        }

        const lines: string[] = [];
        let currentLine = '';
        let maxLineWidth = 0;

        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const tentative = currentLine ? `${currentLine} ${word}` : word;
            const width = ctx.measureText(tentative).width;
            if (width <= maxWidth || maxWidth <= 0) {
                currentLine = tentative;
                maxLineWidth = Math.max(maxLineWidth, width);
            } else {
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                    maxLineWidth = Math.max(maxLineWidth, ctx.measureText(word).width);
                } else {
                    // Single word longer than max width: break by characters
                    const parts = breakLongWordByChars(word, maxWidth, fontSize, fontFamily);
                    for (let p = 0; p < parts.length - 1; p++) {
                        lines.push(parts[p]);
                        maxLineWidth = Math.max(maxLineWidth, ctx.measureText(parts[p]).width);
                    }
                    currentLine = parts[parts.length - 1] || '';
                    if (currentLine) maxLineWidth = Math.max(maxLineWidth, ctx.measureText(currentLine).width);
                }
            }
        }
        if (currentLine) {
            lines.push(currentLine);
            maxLineWidth = Math.max(maxLineWidth, ctx.measureText(currentLine).width);
        }

        const lineHeight = Math.round(fontSize * 1.2);
        const totalHeight = lines.length * lineHeight;
        return { lines, maxLineWidth, lineHeight, totalHeight };
    };

    // Word-wrap a string into positioned runs, keeping URL runs distinct so they
    // can be highlighted and clicked inline (not as a separate list).
    type RichRun = { text: string; x: number; width: number; isUrl: boolean; url?: string };
    type RichTextLayout = { lines: RichRun[][]; lineHeight: number; totalHeight: number };
    const layoutRichText = (
        text: string,
        maxWidth: number,
        fontSize: number,
        fontFamily = 'sans-serif'
    ): RichTextLayout => {
        const lineHeight = Math.round(fontSize * 1.2);
        const ctx = getMeasureContext();
        const cleaned = String(text || '').trim();
        if (!ctx || !cleaned) {
            return { lines: cleaned ? [[]] : [[]], lineHeight, totalHeight: lineHeight };
        }
        ctx.font = `${fontSize}px ${fontFamily}`;
        const spaceW = ctx.measureText(' ').width;
        const words = cleaned.split(/\s+/).filter(Boolean);

        const lines: RichRun[][] = [];
        let current: RichRun[] = [];
        let x = 0;
        const pushLine = () => {
            lines.push(current);
            current = [];
            x = 0;
        };
        const placeChunk = (chunk: string, isUrl: boolean, url?: string) => {
            const w = ctx.measureText(chunk).width;
            if (current.length > 0 && x + spaceW + w > maxWidth) {
                pushLine();
            }
            if (current.length > 0) {
                x += spaceW;
            }
            current.push({ text: chunk, x, width: w, isUrl, url });
            x += w;
        };

        for (const word of words) {
            const isUrl = /^https?:\/\//.test(word);
            const w = ctx.measureText(word).width;
            if (w > maxWidth) {
                // Word longer than the line: break into character chunks (each chunk
                // keeps the same url so the whole link stays clickable).
                const parts = breakLongWordByChars(word, maxWidth, fontSize, fontFamily);
                parts.forEach((part, idx) => {
                    if (idx > 0) pushLine();
                    const pw = ctx.measureText(part).width;
                    if (current.length > 0) x += spaceW;
                    current.push({ text: part, x, width: pw, isUrl, url: isUrl ? word : undefined });
                    x += pw;
                });
                continue;
            }
            placeChunk(word, isUrl, isUrl ? word : undefined);
        }
        if (current.length > 0) pushLine();
        if (lines.length === 0) lines.push([]);

        return { lines, lineHeight, totalHeight: lines.length * lineHeight };
    };

    // Build the rich layout for an off-capture header-band row. Off-capture
    // violations are not drawn on the image, so we show both the violation content
    // and the edit note. URLs embedded in those texts are highlighted and clickable
    // inline. The same descriptor list is used for measuring (height) and rendering
    // so they stay in sync.
    type OffCaptureSegment =
        | { kind: 'label'; text: string; y: number; fontSize: number }
        | { kind: 'richtext'; layout: RichTextLayout; y: number; fontSize: number; muted: boolean }
        | { kind: 'category'; text: string; y: number; fontSize: number };

    const buildOffCaptureSegments = (
        annotation: ViolationData,
        textWidth: number
    ): { segments: OffCaptureSegment[]; totalHeight: number } => {
        const labelFs = 11;
        const textFs = 14;
        const catFs = 12;
        const labelLh = Math.round(labelFs * 1.2);
        const segments: OffCaptureSegment[] = [];
        let y = 12; // top padding inside the box

        const pushField = (label: string, value: string | undefined) => {
            segments.push({ kind: 'label', text: label, y, fontSize: labelFs });
            y += labelLh + 2;
            const cleaned = (value || '').trim();
            const layout = layoutRichText(cleaned || '(未入力)', textWidth, textFs, 'sans-serif');
            segments.push({ kind: 'richtext', layout, y, fontSize: textFs, muted: !cleaned });
            y += layout.totalHeight + 10;
        };

        pushField('キャプチャURL', annotation.captureUrl);
        pushField('違反内容', annotation.violationContent);
        pushField('修正指摘内容', annotation.editNote);

        const cat = annotation.category ? getCategoryTitle(annotation.category) : 'カテゴリ未設定';
        segments.push({ kind: 'category', text: `カテゴリ: ${cat}`, y, fontSize: catFs });
        y += Math.round(catFs * 1.2) + 12; // bottom padding

        return { segments, totalHeight: y };
    };

    // Insert at most one newline when exceeding width (do not wrap into multiple lines)
    const insertSingleNewlineIfOverflow = (
        text: string,
        maxWidth: number,
        fontSize: number,
        fontFamily = 'sans-serif'
    ): { lines: string[]; maxLineWidth: number; lineHeight: number; totalHeight: number } => {
        const ctx = getMeasureContext();
        const cleaned = String(text || '').trim();
        const lineHeight = Math.round(fontSize * 1.2);
        if (!ctx || !cleaned) {
            return { lines: [''], maxLineWidth: 0, lineHeight, totalHeight: lineHeight };
        }
        ctx.font = `${fontSize}px ${fontFamily}`;
        const fullWidth = ctx.measureText(cleaned).width;
        if (fullWidth <= maxWidth || maxWidth <= 0) {
            return { lines: [cleaned], maxLineWidth: fullWidth, lineHeight, totalHeight: lineHeight };
        }

        // Choose a single break so BOTH lines fit when possible
        // Step 1: find last index that keeps left <= maxWidth (prefer whitespace)
        let lastFitIndex = -1;
        let lastFitSpaceIndex = -1;
        for (let i = 0; i < cleaned.length; i++) {
            const left = cleaned.slice(0, i + 1);
            const leftWidth = ctx.measureText(left).width;
            if (leftWidth <= maxWidth) {
                lastFitIndex = i;
                if (/\s/.test(cleaned[i])) lastFitSpaceIndex = i;
            } else {
                break;
            }
        }
        if (lastFitIndex < 0) lastFitIndex = 0;

        // candidate starting from best within left bound
        let breakAt = lastFitSpaceIndex >= 0 ? lastFitSpaceIndex : lastFitIndex;

        // Step 2: try moving break forward to shrink right line until it fits,
        // while ensuring left still fits; prefer to land on whitespace if possible.
        const tryAdvance = () => {
            for (let i = breakAt + 1; i < cleaned.length; i++) {
                const left = cleaned.slice(0, i + 1);
                const right = cleaned.slice(i + 1);
                const leftWidth = ctx.measureText(left.trimEnd()).width;
                const rightWidth = ctx.measureText(right.trimStart()).width;
                if (leftWidth > maxWidth) break; // cannot advance further
                if (rightWidth <= maxWidth) {
                    breakAt = i;
                    return true;
                }
            }
            return false;
        };
        const advanced = tryAdvance();

        // Compose lines
        let first = cleaned.slice(0, breakAt + 1).trimEnd();
        let second = cleaned.slice(breakAt + 1).trimStart();

        // Final guard: if second still exceeds, gradually pull break forward by chars
        // until second fits or left overflows; if impossible, keep as-is.
        let width1 = ctx.measureText(first).width;
        let width2 = ctx.measureText(second).width;
        if (!advanced && width2 > maxWidth) {
            for (let i = breakAt + 1; i < cleaned.length; i++) {
                const left = cleaned.slice(0, i + 1).trimEnd();
                const right = cleaned.slice(i + 1).trimStart();
                const leftWidth = ctx.measureText(left).width;
                const rightWidth = ctx.measureText(right).width;
                if (leftWidth > maxWidth) break;
                first = left;
                second = right;
                width1 = leftWidth;
                width2 = rightWidth;
                if (rightWidth <= maxWidth) break;
            }
        }

        const maxLineWidth = Math.max(width1, width2);
        const lines = [first, second];
        const totalHeight = lines.length * lineHeight;
        return { lines, maxLineWidth, lineHeight, totalHeight };
    };
    const [lastDeletedAnnotation, setLastDeletedAnnotation] = useState<ViolationData | null>(null);
    const stageRef = useRef<any>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const categoryDropdownRef = useRef<HTMLDivElement>(null);
    const violationListRef = useRef<HTMLDivElement>(null); // Ref for violation list container
    const loadingRef = useRef<boolean>(false); // Track if image is currently loading
    const previousViolationsRef = useRef<ViolationData[]>([]); // Track previous violations to prevent unnecessary re-processing
    const attemptedLoadRef = useRef<string | null>(null); // Track which URL we've attempted to load
    const drawTilesCallCountRef = useRef<number>(0); // Track how many times draw tiles effect runs
    const lastDrawDimensionsRef = useRef<{ width: number; height: number } | null>(null); // Track last drawn dimensions

    // Get device pixel ratio for consistent scaling
    const devicePixelRatio = window.devicePixelRatio || 1;

    // ---- Background tiling (render the image as multiple stacked parts, but visually one continuous image) ----
    type ImageTile = { id: string; top: number; height: number; ref: React.RefObject<HTMLCanvasElement> };
    const [imageTiles, setImageTiles] = useState<ImageTile[]>([]);

    // Recompute tiles when the rendered image height changes (helps avoid giant single-image rendering issues)
    useEffect(() => {
        console.log('[ViolationCanvas] Tiling useEffect triggered:', {
            hasImageElement: !!imageElement,
            imageLoaded,
            imageLoading,
            imageRendered,
            imageDimensions: { width: imageDimensions.width, height: imageDimensions.height }
        });

        if (!imageElement || !imageLoaded || imageLoading || !imageRendered) {
            console.log('[ViolationCanvas] Tiling skipped - conditions not met:', {
                hasImageElement: !!imageElement,
                imageLoaded,
                imageLoading,
                imageRendered
            });
            setImageTiles([]);
            return;
        }
        const displayW = Math.floor(imageDimensions.width);
        const displayH = Math.floor(imageDimensions.height);
        if (displayW <= 0 || displayH <= 0) {
            console.log('[ViolationCanvas] Tiling skipped - invalid dimensions:', { displayW, displayH });
            setImageTiles([]);
            return;
        }

        // Choose a tile height that keeps each canvas reasonably sized.
        // (Safari/Chromium can struggle with very tall single images/canvases.)
        const MAX_TILE_HEIGHT = 1400; // display px
        const tiles: ImageTile[] = [];
        let top = 0;
        let idx = 0;
        while (top < displayH) {
            const h = Math.min(MAX_TILE_HEIGHT, displayH - top);
            tiles.push({
                id: `tile-${idx}-${displayW}x${displayH}`,
                top,
                height: h,
                ref: React.createRef<HTMLCanvasElement>(),
            });
            top += h;
            idx += 1;
        }
        console.log('[ViolationCanvas] ✅ Background tiling enabled:', {
            tiles: tiles.length,
            display: { width: displayW, height: displayH },
            maxTileHeight: MAX_TILE_HEIGHT,
            tileHeights: tiles.map(t => t.height),
            naturalImageSize: { width: imageElement.naturalWidth || imageElement.width, height: imageElement.naturalHeight || imageElement.height }
        });
        setImageTiles(tiles);
    }, [imageElement, imageLoaded, imageLoading, imageRendered, imageDimensions.width, imageDimensions.height]);

    // Draw each tile canvas from the source image
    useEffect(() => {
        drawTilesCallCountRef.current += 1;
        const callCount = drawTilesCallCountRef.current;
        const effectTriggerTime = performance.now();
        
        console.log(`[ViolationCanvas] Draw tiles useEffect triggered (call #${callCount}):`, {
            hasImageElement: !!imageElement,
            tilesCount: imageTiles.length,
            timestamp: new Date().toISOString(),
            previousDimensions: lastDrawDimensionsRef.current
        });

        if (!imageElement || imageTiles.length === 0) {
            console.log('[ViolationCanvas] Draw tiles skipped:', {
                hasImageElement: !!imageElement,
                tilesCount: imageTiles.length
            });
            return;
        }
        const naturalW = imageElement.naturalWidth || imageElement.width;
        const naturalH = imageElement.naturalHeight || imageElement.height;
        if (naturalW <= 0 || naturalH <= 0) {
            console.log('[ViolationCanvas] Draw tiles skipped - invalid natural size:', { naturalW, naturalH });
            return;
        }

        const displayW = Math.floor(imageDimensions.width);
        const displayH = Math.floor(imageDimensions.height);
        if (displayW <= 0 || displayH <= 0) {
            console.log('[ViolationCanvas] Draw tiles skipped - invalid display size:', { displayW, displayH });
            return;
        }

        // Check if dimensions changed - if not, we might be re-drawing unnecessarily
        const dimensionsChanged = !lastDrawDimensionsRef.current || 
            lastDrawDimensionsRef.current.width !== displayW || 
            lastDrawDimensionsRef.current.height !== displayH;
        
        if (!dimensionsChanged && callCount > 1) {
            console.warn(`[ViolationCanvas] ⚠️ Draw tiles effect called again (${callCount} times) without dimension change - possible performance issue!`, {
                currentDimensions: { width: displayW, height: displayH },
                previousDimensions: lastDrawDimensionsRef.current
            });
        }
        
        lastDrawDimensionsRef.current = { width: displayW, height: displayH };

        const scale = displayW / naturalW; // uniform scale (we always scale by width)
        const ctxOpts: CanvasRenderingContext2DSettings = { alpha: false, desynchronized: true };

        console.log('[ViolationCanvas] ✅ Drawing background tiles:', {
            tiles: imageTiles.length,
            natural: { width: naturalW, height: naturalH },
            display: { width: displayW, height: displayH },
            scale,
        });

        // Draw all tiles
        const MAX_TILES_TO_DRAW = imageTiles.length; // Draw all tiles
        const tilesToDraw = imageTiles.slice(0, MAX_TILES_TO_DRAW);
        console.log(`[ViolationCanvas] Drawing ${tilesToDraw.length} tiles (out of ${imageTiles.length} total)`);

        const drawStartTime = performance.now();
        let drawnCount = 0;
        tilesToDraw.forEach((tile, index) => {
            const tileStartTime = performance.now();
            const canvas = tile.ref.current;
            if (!canvas) {
                console.log(`[ViolationCanvas] Tile ${index} canvas ref not available yet`);
                return;
            }
            const ctx = canvas.getContext('2d', ctxOpts);
            if (!ctx) {
                console.log(`[ViolationCanvas] Tile ${index} context creation failed`);
                return;
            }

            // Source rect in natural pixels (calculate first)
            const srcY = Math.round(tile.top / scale);
            const srcH = Math.round(tile.height / scale);

            // Destination canvas size (display pixels)
            const canvasPixelCount = displayW * tile.height;
            const sourcePixelCount = naturalW * srcH;
            console.log(`[ViolationCanvas] 🔍 Tile ${index} performance info:`, {
                canvasSize: { width: displayW, height: tile.height },
                canvasPixelCount: canvasPixelCount.toLocaleString(),
                sourceRegion: {
                    srcY,
                    srcH,
                    sourcePixelCount: sourcePixelCount.toLocaleString(),
                    naturalImageSize: { width: naturalW, height: naturalH }
                }
            });

            canvas.width = displayW;
            canvas.height = tile.height;

            const clearStart = performance.now();
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const clearTime = performance.now() - clearStart;

            const drawImageStart = performance.now();
            ctx.drawImage(
                imageElement,
                0,
                srcY,
                naturalW,
                Math.min(srcH, Math.max(0, naturalH - srcY)),
                0,
                0,
                displayW,
                tile.height
            );
            const drawImageTime = performance.now() - drawImageStart;
            const tileTotalTime = performance.now() - tileStartTime;

            drawnCount++;
            console.log(`[ViolationCanvas] ✅ Tile ${index} drawn:`, {
                id: tile.id,
                top: tile.top,
                height: tile.height,
                srcY,
                srcH,
                canvasSize: { width: canvas.width, height: canvas.height },
                performance: {
                    clearTime: `${clearTime.toFixed(2)}ms`,
                    drawImageTime: `${drawImageTime.toFixed(2)}ms`,
                    totalTime: `${tileTotalTime.toFixed(2)}ms`
                }
            });
        });
        const totalDrawTime = performance.now() - drawStartTime;
        console.log(`[ViolationCanvas] ✅ Finished drawing ${drawnCount}/${tilesToDraw.length} tile(s) in ${totalDrawTime.toFixed(2)}ms (DEBUG: only first ${MAX_TILES_TO_DRAW} of ${imageTiles.length} total)`);
    }, [imageElement, imageTiles, imageDimensions.width, imageDimensions.height]);

    // Function to extract and zoom the selected violation box area
    const updateZoomedView = useCallback((annotation: ViolationData | null) => {
        if (!zoomPanelEnabled) {
            setZoomedImageUrl(null);
            setShowZoomedView(false);
            setZoomedViewPosition(null);
            lastZoomPanelRef.current = null;
            processingAnnotationIdRef.current = null;
            return;
        }

        if (!annotation || !imageElement || !hasPosition(annotation)) {
            setZoomedImageUrl(null);
            setShowZoomedView(false);
            setZoomedViewPosition(null);
            lastZoomPanelRef.current = null;
            processingAnnotationIdRef.current = null;
            return;
        }

        // Skip if we're already processing this annotation (prevents duplicate calls)
        if (processingAnnotationIdRef.current === annotation.id) {
            return;
        }

        // Mark as processing to prevent duplicate calls from multiple useEffects
        processingAnnotationIdRef.current = annotation.id;

        // Create a canvas to extract the cropped image
        if (!zoomCanvasRef.current) {
            zoomCanvasRef.current = document.createElement('canvas');
        }
        const canvas = zoomCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Get the original image dimensions
        const originalImageWidth = imageElement.naturalWidth || imageElement.width;
        const originalImageHeight = imageElement.naturalHeight || imageElement.height;

        if (originalImageWidth === 0 || originalImageHeight === 0) {
            // Image not fully loaded yet, wait for it
            const currentAnnotationId = annotation.id;
            const onImageLoad = () => {
                // Re-run the update when image loads, but clear processing ref first
                processingAnnotationIdRef.current = null;
                const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
                if (selectedAnnotation && !selectedAnnotation.deleted && selectedAnnotation.id === currentAnnotationId) {
                    updateZoomedView(selectedAnnotation);
                }
            };
            imageElement.addEventListener('load', onImageLoad, { once: true });
            return;
        }

        // Convert ratio-based coordinates to original image pixel coordinates
        const sourceX = Math.max(0, (annotation.xRatio || 0) * originalImageWidth);
        const sourceY = Math.max(0, (annotation.yRatio || 0) * originalImageHeight);
        const sourceWidth = Math.min((annotation.widthRatio || 0) * originalImageWidth, originalImageWidth - sourceX);
        const sourceHeight = Math.min((annotation.heightRatio || 0) * originalImageHeight, originalImageHeight - sourceY);

        // Set canvas size to the violation box size (with some padding for better view)
        const padding = Math.max(20, Math.min(sourceWidth, sourceHeight) * 0.1); // 10% padding or 20px minimum
        const zoomScale = 3; // Zoom factor to make it larger
        const canvasWidth = (sourceWidth + padding * 2) * zoomScale;
        const canvasHeight = (sourceHeight + padding * 2) * zoomScale;

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Calculate the area to draw with padding (clamped to image bounds)
        const drawX = Math.max(0, sourceX - padding);
        const drawY = Math.max(0, sourceY - padding);
        const drawWidth = Math.min(originalImageWidth - drawX, sourceWidth + padding * 2);
        const drawHeight = Math.min(originalImageHeight - drawY, sourceHeight + padding * 2);

        // Fill with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image portion
        ctx.drawImage(
            imageElement,
            drawX, drawY, drawWidth, drawHeight,
            0, 0, canvas.width, canvas.height
        );

        // Draw a border to highlight the actual violation box area
        const boxX = ((sourceX - drawX) / drawWidth) * canvas.width;
        const boxY = ((sourceY - drawY) / drawHeight) * canvas.height;
        const boxWidth = (sourceWidth / drawWidth) * canvas.width;
        const boxHeight = (sourceHeight / drawHeight) * canvas.height;

        ctx.strokeStyle = isUnfixableViolation(annotation)
            ? UNFIXABLE_BOX_STROKE
            : DEFAULT_BOX_STROKE;
        ctx.lineWidth = 3;
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

        // Convert canvas to data URL (stored for later assignment after position is finalized)
        const dataUrl = canvas.toDataURL('image/png');

        // Calculate headerHeight for unpositioned violations (respect sidebar filters)
        const visibleAnnotations = annotations.filter(passesCanvasVisibility);
        const unpositioned = [
            ...visibleAnnotations.filter(isOverallPageScope),
            ...visibleAnnotations.filter(isOffCaptureScope),
        ];
        const headerPadding = unpositioned.length > 0 ? 20 : 0;
        const headerTitleHeight = unpositioned.length > 0 ? 28 : 0;
        const headerRowGap = 10;
        const headerSeparatorGap = unpositioned.length > 0 ? 20 : 0;

        const measuredUnpositioned = (() => {
            if (unpositioned.length === 0) return [] as Array<{ boxY: number; boxH: number }>;
            const results: Array<{ boxY: number; boxH: number }> = [];
            let cursorY = headerPadding / 2 + headerTitleHeight;
            const boxW = Math.max(0, imageDimensions.width - 20);
            const textWidth = Math.max(60, boxW - 24);
            const titleFontSize = 14;
            const minRowHeight = 70;
            const categoryBlockHeight = 24;

            unpositioned.forEach((ann) => {
                if (isOffCaptureScope(ann)) {
                    const { totalHeight } = buildOffCaptureSegments(ann, textWidth);
                    const boxH = Math.max(minRowHeight, Math.ceil(totalHeight));
                    results.push({ boxY: cursorY, boxH });
                    cursorY += boxH + headerRowGap;
                    return;
                }
                const title = getHeaderRowTitle(ann);
                const wrapped = wrapTextToWidth(String(title), textWidth, titleFontSize, 'sans-serif');
                const titleHeight = Math.max(wrapped.totalHeight, titleFontSize * 1.2);
                const contentHeight = titleHeight + 12 + categoryBlockHeight + 12;
                const boxH = Math.max(minRowHeight, Math.ceil(contentHeight));
                results.push({ boxY: cursorY, boxH });
                cursorY += boxH + headerRowGap;
            });
            return results;
        })();

        const currentHeaderHeight = measuredUnpositioned.length > 0
            ? Math.ceil((measuredUnpositioned[measuredUnpositioned.length - 1].boxY + measuredUnpositioned[measuredUnpositioned.length - 1].boxH) - (headerPadding / 2) + headerPadding / 2 + headerSeparatorGap)
            : 0;

        // Calculate position near the edit note label
        const canvasContainer = document.getElementById('canvas-container');
        if (canvasContainer) {
            // Calculate the position of the edit note label
            // For positioned violations, coordinates are relative to the image area (not including header)
            const groupX = ratioToPixels(annotation.xRatio || 0, imageDimensions.width);
            // Note: headerHeight is only for unpositioned violations in the header area
            // For positioned violations, yRatio is relative to the image, which starts at headerHeight in the stage
            const groupY = currentHeaderHeight + ratioToPixels(annotation.yRatio || 0, imageDimensions.height);
            const layout = computeLabelLayout(annotation, imageDimensions.width, imageDimensions.height, 14);

            // Label position relative to the stage
            const labelX = groupX;
            const labelY = groupY + layout.textY; // textY is negative, so this is above the box
            const labelWidth = layout.bgWidth;

            // Stage is centered, so we need to account for that
            // The content div has padding: 20px, and the stage container is at top: 20px, left: 50% (centered)
            const contentPadding = 20; // Content div padding
            const stageContainerTop = 20; // Stage container top position (relative to content div)

            // Calculate stage left position relative to canvas-container
            // Content div inner width = containerWidth - 40 (20px padding each side)
            // Stage is centered: left = 50% of content div inner width, then translateX(-50%)
            // Stage left edge relative to content div = (innerWidth / 2) - (imageWidth / 2)
            // Stage left edge relative to canvas-container = contentPadding + (innerWidth / 2) - (imageWidth / 2)
            const contentInnerWidth = canvasContainer.clientWidth - contentPadding * 2;
            const stageLeft = contentPadding + (contentInnerWidth / 2) - (imageDimensions.width / 2);

            // Calculate the label's position relative to the canvas container
            // Account for: stage container position + label position within stage
            const labelContainerX = stageLeft + labelX;

            // Calculate label Y position relative to canvas-container
            // Structure: canvas-container > content-div (padding: 20px) > stage-container (top: 20px, absolute)
            // So stage container top relative to canvas-container = contentPadding (20) + stageContainerTop (20) = 40px
            // labelY is relative to the stage (can be negative when label is above violation box)
            // We need to add the stage container's position to get canvas-container coordinates
            const stageContainerTopAbsolute = contentPadding + stageContainerTop; // 40px from canvas-container top
            let labelContainerY = stageContainerTopAbsolute + labelY;

            // If labelY is very negative (label far above violation), ensure we don't go negative
            // But we want to show the panel near the label, so if label is above visible area,
            // position panel at the violation box position instead
            if (labelContainerY < 0) {
                // Label is above visible area, use violation box Y position instead
                const violationBoxY = stageContainerTopAbsolute + groupY;
                labelContainerY = Math.max(20, violationBoxY); // Use violation box position, minimum 20px margin
            }

            // Calculate violation box width in viewport pixels
            const violationBoxWidth = ratioToPixels(annotation.widthRatio || 0, imageDimensions.width);
            const violationBoxHeight = ratioToPixels(annotation.heightRatio || 0, imageDimensions.height);

            // Calculate padding (same logic as in canvas extraction)
            const padding = Math.max(20, Math.min(violationBoxWidth, violationBoxHeight) * 0.1); // 10% padding or 20px minimum

            // Calculate dynamic panel width based on violation box width
            // Canvas extraction uses: (sourceWidth + padding * 2) * zoomScale
            // We need to account for this in the panel width
            const zoomScale = 3; // Same as in canvas extraction
            const containerWidth = canvasContainer.clientWidth;
            const minPanelWidth = 300; // Minimum panel width
            const maxPanelWidth = Math.min(800, containerWidth * 0.7); // Max 70% of container or 800px
            const basePanelWidth = (violationBoxWidth + padding * 2) * zoomScale; // Match canvas width calculation

            // Clamp the panel width between min and max
            const panelWidth = Math.max(minPanelWidth, Math.min(maxPanelWidth, basePanelWidth));
            const spacing = 10; // Minimum spacing from violation box and label
            const minPanelHeight = 200;
            const maxPanelHeight = Math.min(800, canvasContainer.clientHeight * 0.9);
            const basePanelHeight = Math.max(violationBoxHeight * zoomScale + layout.bgHeight + spacing * 2, minPanelHeight);
            const panelHeight = Math.max(minPanelHeight, Math.min(maxPanelHeight, basePanelHeight));

            // Calculate violation box bounds in container coordinates
            const violationBoxContainerX = stageLeft + groupX;
            const violationBoxContainerY = stageContainerTopAbsolute + groupY;
            const violationBoxContainerWidth = violationBoxWidth;
            const violationBoxContainerHeight = violationBoxHeight;
            const violationBounds = {
                startX: violationBoxContainerX,
                stopX: violationBoxContainerX + violationBoxContainerWidth,
                startY: violationBoxContainerY,
                stopY: violationBoxContainerY + violationBoxContainerHeight
            };

            const previousPanelState = lastZoomPanelRef.current;
            const isNewPanelForAnnotation = !previousPanelState || previousPanelState.id !== annotation.id;

            // Calculate label bounds in container coordinates
            const labelContainerHeight = layout.bgHeight;
            const labelTop = labelContainerY; // labelContainerY is the top of the label (since labelY can be negative)
            const labelBottom = labelTop + labelContainerHeight;
            const labelLeft = labelContainerX;
            const labelRight = labelContainerX + labelWidth;

            // Calculate position relative to canvas container (absolute positioning)
            // Try to position to the right of the label first
            let finalX = labelContainerX + labelWidth + spacing; // spacing from label
            let finalY = labelTop + (labelContainerHeight / 2) - (panelHeight / 2);
            if (isNewPanelForAnnotation) {
                console.log('Zoom panel: initial placement before overlap checks', {
                    violationBounds,
                    labelBounds: {
                        startX: labelLeft,
                        stopX: labelRight,
                        startY: labelTop,
                        stopY: labelBottom
                    },
                    panelInitial: {
                        startX: finalX,
                        stopX: finalX + panelWidth,
                        startY: finalY,
                        stopY: finalY + panelHeight
                    }
                });
            }

            // Make sure the panel doesn't go off screen
            // Use clientHeight for viewport bounds, but allow positioning in scrollable content
            const containerViewportHeight = canvasContainer.clientHeight;
            const containerContentHeight = canvasContainer.scrollHeight;
            const minTopMargin = 20; // Minimum margin from top of container

            // Helper function to check if a position overlaps with label or violation box
            const checkOverlap = (x: number, y: number): {
                overlapsLabel: boolean;
                overlapsViolationBox: boolean;
                labelHorizontalSeparated: boolean;
                labelVerticalSeparated: boolean;
                boxHorizontalSeparated: boolean;
                boxVerticalSeparated: boolean;
            } => {
                const right = x + panelWidth;
                const bottom = y + panelHeight;
                const left = x;
                const top = y;

                const labelHorizontalSeparated = right <= labelLeft - spacing || left >= labelRight + spacing;
                const labelVerticalSeparated = bottom <= labelTop - spacing || top >= labelBottom + spacing;
                const overlapsLabel = !(labelHorizontalSeparated || labelVerticalSeparated);

                const boxHorizontalSeparated = right <= violationBounds.startX - spacing || left >= violationBounds.stopX + spacing;
                const boxVerticalSeparated = bottom <= violationBounds.startY - spacing || top >= violationBounds.stopY + spacing;
                const overlapsViolationBox = !(boxHorizontalSeparated || boxVerticalSeparated);

                return {
                    overlapsLabel,
                    overlapsViolationBox,
                    labelHorizontalSeparated,
                    labelVerticalSeparated,
                    boxHorizontalSeparated,
                    boxVerticalSeparated
                };
            };

            // If panel overlaps with label or violation box, try alternative positions
            const initialOverlap = checkOverlap(finalX, finalY);

            let overlapAfterHorizontal = initialOverlap;
            if (initialOverlap.overlapsLabel || initialOverlap.overlapsViolationBox) {
                if (initialOverlap.overlapsLabel) {
                    console.log('Zoom panel: overlap detected with label', {
                        reason: {
                            horizontalSeparated: initialOverlap.labelHorizontalSeparated,
                            verticalSeparated: initialOverlap.labelVerticalSeparated
                        },
                        labelBounds: {
                            startX: labelLeft,
                            stopX: labelRight,
                            startY: labelTop,
                            stopY: labelBottom
                        },
                        panelBounds: {
                            startX: finalX,
                            stopX: finalX + panelWidth,
                            startY: finalY,
                            stopY: finalY + panelHeight
                        }
                    });
                }
                if (initialOverlap.overlapsViolationBox) {
                    console.log('Zoom panel: overlap detected with violation box', {
                        reason: {
                            horizontalSeparated: initialOverlap.boxHorizontalSeparated,
                            verticalSeparated: initialOverlap.boxVerticalSeparated
                        },
                        violationBounds,
                        panelBounds: {
                            startX: finalX,
                            stopX: finalX + panelWidth,
                            startY: finalY,
                            stopY: finalY + panelHeight
                        }
                    });
                }

                // Try positioning to the left of the label
                const leftX = labelContainerX - panelWidth - spacing;
                if (leftX >= 20) {
                    const leftOverlap = checkOverlap(leftX, finalY);
                    console.log('Zoom panel: trying left placement', {
                        candidateX: leftX,
                        overlap: leftOverlap
                    });
                    if (!leftOverlap.overlapsLabel && !leftOverlap.overlapsViolationBox) {
                        finalX = leftX;
                        overlapAfterHorizontal = leftOverlap;
                    }
                }

                // If still overlapping, try shifting further right (keeping vertical position)
                if (overlapAfterHorizontal.overlapsLabel || overlapAfterHorizontal.overlapsViolationBox) {
                    const desiredRightX = Math.max(labelRight + spacing, violationBounds.stopX + spacing);
                    const rightX = Math.max(finalX, desiredRightX);
                    const clampedRightX = Math.min(Math.max(20, rightX), containerWidth - panelWidth - 20);
                    const rightOverlap = checkOverlap(clampedRightX, finalY);
                    console.log('Zoom panel: trying right placement', {
                        desiredRightX,
                        candidateX: clampedRightX,
                        overlap: rightOverlap
                    });
                    if (!rightOverlap.overlapsLabel && !rightOverlap.overlapsViolationBox) {
                        finalX = clampedRightX;
                        overlapAfterHorizontal = rightOverlap;
                    }
                }
            }

            // Make sure the panel doesn't go off screen horizontally
            if (finalX + panelWidth > containerWidth - 20) {
                // Try left side
                const leftX = Math.max(20, labelContainerX - panelWidth - spacing);
                if (leftX >= 20) {
                    finalX = leftX;
                } else {
                    // Clamp to container bounds
                    finalX = Math.max(20, Math.min(finalX, containerWidth - panelWidth - 20));
                }
            }
            finalX = Math.min(finalX, containerWidth - panelWidth - 20);
            finalX = Math.max(20, finalX);

            // Adjust Y position - allow positioning in scrollable content area
            // But try to keep panel visible in viewport when possible
            const scrollTop = canvasContainer.scrollTop;
            const viewportTop = scrollTop;
            const viewportBottom = scrollTop + containerViewportHeight;

            // Ensure panel doesn't overlap with label or violation box after X adjustment
            const finalOverlap = overlapAfterHorizontal.overlapsLabel || overlapAfterHorizontal.overlapsViolationBox
                ? overlapAfterHorizontal
                : checkOverlap(finalX, finalY);

            // If still overlapping, adjust Y position
            if (finalOverlap.overlapsLabel) {
                console.log('Zoom panel: still overlapping label after horizontal adjustments', {
                    overlap: finalOverlap,
                    labelBounds: {
                        startX: labelLeft,
                        stopX: labelRight,
                        startY: labelTop,
                        stopY: labelBottom
                    },
                    panelBounds: {
                        startX: finalX,
                        stopX: finalX + panelWidth,
                        startY: finalY,
                        stopY: finalY + panelHeight
                    }
                });
                // Position above or below label
                const aboveLabelY = labelTop - panelHeight - spacing;
                const belowLabelY = labelBottom + spacing;
                if (aboveLabelY >= minTopMargin) {
                    const aboveOverlap = checkOverlap(finalX, aboveLabelY);
                    if (!aboveOverlap.overlapsLabel && !aboveOverlap.overlapsViolationBox) {
                        console.log('Zoom panel: moving above label to resolve overlap', {
                            attempt: 'label-above',
                            proposedTop: aboveLabelY,
                            aboveOverlap
                        });
                        finalY = aboveLabelY;
                    } else {
                        console.log('Zoom panel: above label still overlaps, placing below label', {
                            attempt: 'label-below',
                            proposedTop: belowLabelY,
                            aboveOverlap
                        });
                        finalY = belowLabelY;
                    }
                } else {
                    console.log('Zoom panel: cannot place above label, placing below label', {
                        attempt: 'label-below-no-above-space',
                        proposedTop: belowLabelY
                    });
                    finalY = belowLabelY;
                }
            } else if (finalOverlap.overlapsViolationBox) {
                console.log('Zoom panel: still overlapping violation box after horizontal adjustments', {
                    overlap: finalOverlap,
                    violationBounds,
                    panelBounds: {
                        startX: finalX,
                        stopX: finalX + panelWidth,
                        startY: finalY,
                        stopY: finalY + panelHeight
                    }
                });
                // Position above or below violation box
                const aboveBoxY = violationBoxContainerY - panelHeight - spacing;
                const belowBoxY = violationBoxContainerY + violationBoxContainerHeight + spacing;
                if (aboveBoxY >= minTopMargin) {
                    const aboveOverlap = checkOverlap(finalX, aboveBoxY);
                    if (!aboveOverlap.overlapsLabel && !aboveOverlap.overlapsViolationBox) {
                        console.log('Zoom panel: moving above violation box to resolve overlap', {
                            attempt: 'box-above',
                            proposedTop: aboveBoxY,
                            aboveOverlap
                        });
                        finalY = aboveBoxY;
                    } else {
                        console.log('Zoom panel: above violation box still overlaps, placing below box', {
                            attempt: 'box-below',
                            proposedTop: belowBoxY,
                            aboveOverlap
                        });
                        finalY = belowBoxY;
                    }
                } else {
                    console.log('Zoom panel: cannot place above violation box, placing below box', {
                        attempt: 'box-below-no-above-space',
                        proposedTop: belowBoxY
                    });
                    finalY = belowBoxY;
                }
            }

            // If label is in viewport, try to keep panel in viewport
            if (labelContainerY >= viewportTop && labelContainerY <= viewportBottom) {
                // If panel would go below viewport, try positioning above
                if (finalY + panelHeight > viewportBottom - 20) {
                    const aboveY = labelTop - panelHeight - spacing;
                    if (aboveY >= viewportTop + 20 && aboveY >= minTopMargin) {
                        const aboveOverlap = checkOverlap(finalX, aboveY);
                        if (!aboveOverlap.overlapsLabel && !aboveOverlap.overlapsViolationBox) {
                            finalY = aboveY;
                        } else {
                            finalY = Math.max(viewportTop + 20, labelContainerY);
                        }
                    } else {
                        finalY = Math.max(viewportTop + 20, labelContainerY);
                    }
                }
            }

            // Ensure panel doesn't go above content area
            if (finalY < minTopMargin) {
                finalY = minTopMargin;
            }
            // Ensure panel doesn't go below content area (but allow it to be in scrollable area)
            finalY = Math.min(finalY, containerContentHeight - panelHeight - 20);

            // Final pass: adjust Y incrementally to avoid overlap
            const maxPanelBottom = containerContentHeight - panelHeight - 20;
            const clampY = (value: number) => Math.min(maxPanelBottom, Math.max(minTopMargin, value));
            let currentY = clampY(finalY);
            let overlapCheck = checkOverlap(finalX, currentY);

            if (overlapCheck.overlapsLabel || overlapCheck.overlapsViolationBox) {
                console.log('Zoom panel: overlap remains after initial placement, starting incremental adjustment', {
                    startTop: currentY,
                    overlap: overlapCheck
                });
                let incrementAttempts = 0;
                while (incrementAttempts < 10 && (overlapCheck.overlapsLabel || overlapCheck.overlapsViolationBox)) {
                    const nextY = clampY(currentY + 20);
                    if (nextY === currentY) {
                        break;
                    }
                    currentY = nextY;
                    overlapCheck = checkOverlap(finalX, currentY);
                    incrementAttempts++;
                    console.log('Zoom panel: increment adjustment attempt', {
                        attempt: incrementAttempts,
                        newTop: currentY,
                        overlap: overlapCheck
                    });
                }

                if (overlapCheck.overlapsLabel || overlapCheck.overlapsViolationBox) {
                    console.log('Zoom panel: overlap after increment adjustments, trying decrements', {
                        currentTop: currentY,
                        overlap: overlapCheck
                    });
                    let decrementAttempts = 0;
                    while (decrementAttempts < 10 && (overlapCheck.overlapsLabel || overlapCheck.overlapsViolationBox)) {
                        const nextY = clampY(currentY - 20);
                        if (nextY === currentY) {
                            break;
                        }
                        currentY = nextY;
                        overlapCheck = checkOverlap(finalX, currentY);
                        decrementAttempts++;
                        console.log('Zoom panel: decrement adjustment attempt', {
                            attempt: decrementAttempts,
                            newTop: currentY,
                            overlap: overlapCheck
                        });
                    }
                }

                if (!(overlapCheck.overlapsLabel || overlapCheck.overlapsViolationBox)) {
                    console.log('Zoom panel: overlap resolved after incremental adjustments', {
                        finalTop: currentY
                    });
                } else {
                    console.warn('Zoom panel: unable to fully resolve overlap after incremental adjustments', {
                        finalTop: currentY,
                        overlap: overlapCheck
                    });
                }
            }

            finalY = currentY;
            console.log('Zoom panel: final placement after adjustments', {
                violationBounds,
                finalPanel: {
                    startX: finalX,
                    stopX: finalX + panelWidth,
                    startY: finalY,
                    stopY: finalY + panelHeight
                }
            });

            const panelState = { top: finalY, left: finalX, width: panelWidth };
            const newPanelCache = { id: annotation.id, ...panelState };
            const lastPanelState = previousPanelState;
            const positionChanged =
                !lastPanelState ||
                lastPanelState.id !== newPanelCache.id ||
                Math.abs(lastPanelState.top - newPanelCache.top) > 0.5 ||
                Math.abs(lastPanelState.left - newPanelCache.left) > 0.5 ||
                Math.abs(lastPanelState.width - newPanelCache.width) > 0.5;

            if (positionChanged) {
                lastZoomPanelRef.current = newPanelCache;

                // Log zoom panel creation with position details
                console.log('=== ZOOM PANEL CREATED ===');
                console.log('Violation ID:', annotation.id);
                console.log('Violation box dimensions:', {
                    width: violationBoxWidth,
                    height: violationBoxHeight,
                    xRatio: annotation.xRatio,
                    yRatio: annotation.yRatio,
                    widthRatio: annotation.widthRatio,
                    heightRatio: annotation.heightRatio
                });
                console.log('Label position:', {
                    labelX,
                    labelY,
                    labelWidth,
                    labelContainerX,
                    labelContainerY,
                    groupY,
                    stageContainerTopAbsolute,
                    textY: layout.textY
                });
                console.log('Panel dimensions:', {
                    width: panelWidth,
                    height: panelHeight,
                    minWidth: minPanelWidth,
                    maxWidth: maxPanelWidth,
                    baseWidth: basePanelWidth,
                    padding
                });
                console.log('Panel position:', {
                    top: finalY,
                    left: finalX,
                    containerWidth,
                    containerViewportHeight,
                    containerContentHeight,
                    scrollTop: canvasContainer.scrollTop,
                    viewportTop,
                    viewportBottom,
                    stageLeft,
                    contentPadding,
                    labelContainerY,
                    labelY,
                    groupY,
                    currentHeaderHeight
                });
                console.log('=== END ZOOM PANEL CREATED ===');
            }

            setZoomedViewPosition(panelState);
        } else {
            // Fallback to top-left if container not found
            console.warn('Zoom panel: Canvas container not found, using fallback position');
            setZoomedViewPosition({ top: 20, left: 24, width: 400 });
        }

        // Update the zoom image after final position has been determined
        setZoomedImageUrl(dataUrl);

        setShowZoomedView(true);

        // Clear processing flag - use requestAnimationFrame to clear after state update
        // This allows the current call to complete but prevents rapid duplicate calls
        requestAnimationFrame(() => {
            if (processingAnnotationIdRef.current === annotation.id) {
                processingAnnotationIdRef.current = null;
            }
        });
    }, [imageElement, annotations, selectedAnnotationId, imageDimensions, zoomPanelEnabled]);

    // Viewport-based Konva Stage repositioning (performance optimization)
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        function repositionStage() {
            const currentScrollContainer = scrollContainerRef.current;
            if (!currentScrollContainer) return;
            
            const PADDING = 500; // Padding around viewport
            const dx = currentScrollContainer.scrollLeft - PADDING;
            const dy = currentScrollContainer.scrollTop - PADDING;
            
            if (stageContainerRef.current) {
                stageContainerRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
            }
            setStagePosition({ x: -dx, y: -dy });
        }

        scrollContainer.addEventListener('scroll', repositionStage, { passive: true });
        repositionStage(); // Initial positioning

        return () => {
            scrollContainer.removeEventListener('scroll', repositionStage);
        };
    }, []);

    const isEditingAnnotation = Boolean(editingAnnotationId);

    // Cropped preview of the violation area shown inside the edit modal, so reviewers
    // can confirm what was flagged without closing the popup. Computed independently of
    // the floating zoom panel (which is hidden while editing), using the same crop math.
    const editingAnnotationImageUrl = useMemo(() => {
        if (!editingAnnotationId || !imageElement) return null;
        const annotation = annotations.find(a => a.id === editingAnnotationId);
        if (!annotation || annotation.scope === 'off_capture' || !hasPosition(annotation)) return null;

        const originalImageWidth = imageElement.naturalWidth || imageElement.width;
        const originalImageHeight = imageElement.naturalHeight || imageElement.height;
        if (originalImageWidth === 0 || originalImageHeight === 0) return null;

        const sourceX = Math.max(0, (annotation.xRatio || 0) * originalImageWidth);
        const sourceY = Math.max(0, (annotation.yRatio || 0) * originalImageHeight);
        const sourceWidth = Math.min((annotation.widthRatio || 0) * originalImageWidth, originalImageWidth - sourceX);
        const sourceHeight = Math.min((annotation.heightRatio || 0) * originalImageHeight, originalImageHeight - sourceY);
        if (sourceWidth <= 0 || sourceHeight <= 0) return null;

        const padding = Math.max(20, Math.min(sourceWidth, sourceHeight) * 0.1);
        const zoomScale = 3;
        const canvas = document.createElement('canvas');
        canvas.width = (sourceWidth + padding * 2) * zoomScale;
        canvas.height = (sourceHeight + padding * 2) * zoomScale;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        const drawX = Math.max(0, sourceX - padding);
        const drawY = Math.max(0, sourceY - padding);
        const drawWidth = Math.min(originalImageWidth - drawX, sourceWidth + padding * 2);
        const drawHeight = Math.min(originalImageHeight - drawY, sourceHeight + padding * 2);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageElement, drawX, drawY, drawWidth, drawHeight, 0, 0, canvas.width, canvas.height);

        const boxX = ((sourceX - drawX) / drawWidth) * canvas.width;
        const boxY = ((sourceY - drawY) / drawHeight) * canvas.height;
        const boxWidth = (sourceWidth / drawWidth) * canvas.width;
        const boxHeight = (sourceHeight / drawHeight) * canvas.height;

        ctx.strokeStyle = '#ff00ff';
        ctx.lineWidth = 3;
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

        return canvas.toDataURL('image/png');
    }, [editingAnnotationId, annotations, imageElement]);

    const generateId = () => {
        // Generate a proper UUID v4 format
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Create a new unpositioned violation and start editing it
    const addUnpositionedViolation = () => {
        const newAnnotation: ViolationData = {
            id: generateId(),
            category: '',
            severity: 'medium',
            violationType: 'manual_created',
            scope: 'overall_page',
            violationContent: '',
            editNote: '',
            // No position fields set (unpositioned)
            confidence: 0,
            createdAt: new Date().toISOString(),
            createdBy: 'user',
            isSelected: false,
            deleted: false,
            categoryContentExistence: false,
            categoryContentViolationExistence: true,
        };

        updateAnnotations(prev => {
            const updated = [...prev, newAnnotation];
            return updated;
        });

        // Select and open editor
        setSelectedAnnotationId(newAnnotation.id);
        setTimeout(() => {
            setEditingAnnotationId(newAnnotation.id);
            setEditText('');
            setEditContent('');
            setEditCaptureUrl('');
            setSelectedCategory('');
        }, 50);
    };

    // Create a new "off capture" violation (not visible in the capture because
    // full-page capture failed) and start editing it. Unlike overall-page
    // violations, these keep both violation content and edit note.
    const addOffCaptureViolation = () => {
        const newAnnotation: ViolationData = {
            id: generateId(),
            category: '',
            severity: 'medium',
            violationType: 'manual_created',
            scope: 'off_capture',
            violationContent: '',
            editNote: '',
            // No position fields set (never rendered on the canvas)
            confidence: 0,
            createdAt: new Date().toISOString(),
            createdBy: 'user',
            isSelected: false,
            deleted: false,
            categoryContentExistence: false,
            categoryContentViolationExistence: true,
        };

        updateAnnotations(prev => [...prev, newAnnotation]);

        setSelectedAnnotationId(newAnnotation.id);
        setTimeout(() => {
            setEditingAnnotationId(newAnnotation.id);
            setEditText('');
            setEditContent('');
            setEditCaptureUrl('');
            setSelectedCategory('');
        }, 50);
    };

    // Helper function to call the callback when violations change
    const notifyViolationsChange = useCallback((newAnnotations: ViolationData[]) => {
        console.log('=== NOTIFY VIOLATIONS CHANGE ===');
        console.log('Total annotations to notify:', newAnnotations.length);
        newAnnotations.forEach((annotation, index) => {
            console.log(`Annotation ${index + 1}:`);
            console.log(`  - ID: ${annotation.id}`);
            console.log(`  - deleted: ${annotation.deleted}`);
            console.log(`  - violationContent: ${annotation.violationContent}`);
            console.log(`  - violation_type: ${annotation.violationType}`);
        });
        console.log('=== END NOTIFY VIOLATIONS CHANGE ===');

        if (onViolationsChange) {
            onViolationsChange(newAnnotations, []); // Empty array for deletedViolationIds since we don't use it anymore
        }
    }, [onViolationsChange]);

    const updateAnnotations = useCallback((updater: (prev: ViolationData[]) => ViolationData[]) => {
        setAnnotations(prev => {
            const newAnnotations = updater(prev);
            notifyViolationsChange(newAnnotations);
            return newAnnotations;
        });
    }, [notifyViolationsChange]);

    const handleFixabilityChange = useCallback((annotationId: string, fixability: ViolationFixability) => {
        updateAnnotations((prev) =>
            prev.map((a) => {
                if (a.id !== annotationId) return a;
                if (fixability === 'unfixable') {
                    const part =
                        a.unfixableReasonDescriptionPart
                        || findMatchingMediaRuleExcerpt(
                            pageUnfixableRuleDescription,
                            a.editNote,
                            a.violationContent
                        )
                        || undefined;
                    return {
                        ...a,
                        fixability,
                        fixabilitySource: 'manual',
                        unfixableReasonDescriptionPart: part,
                    };
                }
                return {
                    ...a,
                    fixability,
                    fixabilitySource: 'manual',
                    unfixableReasonDescriptionPart: undefined,
                };
            })
        );
    }, [updateAnnotations, pageUnfixableRuleDescription]);

    const handleUnfixableReasonPartChange = useCallback(
        (annotationId: string, part: string) => {
            updateAnnotations((prev) =>
                prev.map((a) =>
                    a.id === annotationId
                        ? {
                              ...a,
                              unfixableReasonDescriptionPart: part || undefined,
                              fixabilitySource: 'manual',
                          }
                        : a
                )
            );
        },
        [updateAnnotations]
    );

    // Clear selection if selected annotation is deleted (unless we're explicitly showing deleted).
    useEffect(() => {
        if (selectedAnnotationId) {
            const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
            if (!selectedAnnotation || (!allowDeletedInView && selectedAnnotation.deleted)) {
                console.log('[ViolationCanvas] clearing selection: deleted guard', {
                    selectedAnnotationId,
                    found: Boolean(selectedAnnotation),
                    allowDeletedInView,
                    deleted: Boolean(selectedAnnotation?.deleted),
                });
                setSelectedAnnotationId(null);
            }
        }
    }, [annotations, selectedAnnotationId, allowDeletedInView]);

    // Clear processing ref when selection changes to allow new annotation to be processed
    useEffect(() => {
        processingAnnotationIdRef.current = null;
    }, [selectedAnnotationId]);

    // Update zoomed view when selection changes
    // Note: Panel will be created immediately, but position will be updated after scroll completes
    // This is handled by scrollToAnnotation which calls updateZoomedView after 600ms
    useEffect(() => {
        if (isEditingAnnotation) {
            updateZoomedView(null);
            return;
        }

        if (zoomPanelEnabled && selectedAnnotationId && imageElement) {
            const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
            if (selectedAnnotation && (allowDeletedInView || !selectedAnnotation.deleted)) {
                // Create panel immediately - position will be corrected after scroll if needed
                updateZoomedView(selectedAnnotation);
            } else {
                updateZoomedView(null);
            }
        } else {
            updateZoomedView(null);
        }
    }, [selectedAnnotationId, annotations, imageElement, zoomPanelEnabled, updateZoomedView, isEditingAnnotation, allowDeletedInView]);

    // Update zoomed view position when window resizes or canvas scrolls
    useEffect(() => {
        if (showZoomedView && selectedAnnotationId && imageElement) {
            const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
            if (selectedAnnotation && !selectedAnnotation.deleted && hasPosition(selectedAnnotation)) {
                // Recalculate position after a short delay to ensure DOM is updated
                const timeoutId = setTimeout(() => {
                    updateZoomedView(selectedAnnotation);
                }, 100);
                return () => clearTimeout(timeoutId);
            }
        }
    }, [imageDimensions, showZoomedView, selectedAnnotationId, annotations, imageElement, updateZoomedView]);

    // Update position on scroll
    useEffect(() => {
        if (!showZoomedView) return;

        const canvasContainer = document.getElementById('canvas-container');
        if (!canvasContainer) return;

        let scrollTimeout: NodeJS.Timeout | null = null;
        let lastScrollTop = canvasContainer.scrollTop;
        let lastScrollLeft = canvasContainer.scrollLeft;

        const updatePosition = () => {
            // Check if scroll actually changed
            const currentScrollTop = canvasContainer.scrollTop;
            const currentScrollLeft = canvasContainer.scrollLeft;

            // Only update if scroll position actually changed
            if (currentScrollTop === lastScrollTop && currentScrollLeft === lastScrollLeft) {
                return;
            }

            lastScrollTop = currentScrollTop;
            lastScrollLeft = currentScrollLeft;

            // Throttle scroll updates to avoid too many recalculations
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(() => {
                if (selectedAnnotationId && imageElement) {
                    const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
                    if (selectedAnnotation && !selectedAnnotation.deleted && hasPosition(selectedAnnotation)) {
                        updateZoomedView(selectedAnnotation);
                    }
                }
            }, 100); // Delay to ensure scroll has settled
        };

        canvasContainer.addEventListener('scroll', updatePosition, { passive: true });
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, { passive: true });

        return () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            canvasContainer.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [showZoomedView, selectedAnnotationId, annotations, imageElement, updateZoomedView]);

    // Helper function to ensure violations have UI state fields
    const ensureUIStateFields = (violations: ViolationData[]): ViolationData[] => {
        return violations.map((violation: any) => {
            // Normalize various deletion flags that may come from backend
            const backendDeleted = (
                violation.deleted === true ||
                violation.deleted === 'true' ||
                violation.is_deleted === true ||
                violation.isDeleted === true ||
                violation.status === 'deleted' ||
                (typeof violation.deletedAt !== 'undefined' && violation.deletedAt !== null)
            );

            return {
                ...violation,
                isSelected: violation.isSelected ?? false,
                deleted: backendDeleted || (violation.deleted ?? false),
                confidence: violation.confidence ?? 0,
                categoryContentExistence: violation.categoryContentExistence ?? false,
                categoryContentViolationExistence: violation.categoryContentViolationExistence ?? false,
            } as ViolationData;
        });
    };

    // Function to export all violation data for debugging/development purposes
    const exportAnnotationPositions = () => {
        // Export all annotations including deleted ones
        const allAnnotationData = annotations.map(annotation => ({
            id: annotation.id,
            xRatio: annotation.xRatio || 0,
            yRatio: annotation.yRatio || 0,
            widthRatio: annotation.widthRatio || 0,
            heightRatio: annotation.heightRatio || 0,
            violationContent: annotation.violationContent,
            editNote: annotation.editNote,
            category: annotation.category,
            isSelected: annotation.isSelected,
            deleted: annotation.deleted || false,
            violationType: annotation.violationType,
        }));

        // Separate deleted and non-deleted violations
        const nonDeletedViolations = allAnnotationData.filter(v => !v.deleted);
        const deletedViolations = allAnnotationData.filter(v => v.deleted);

        console.log('=== ALL VIOLATION DATA FOR DEBUGGING ===');
        console.log('Total violations:', allAnnotationData.length);
        console.log('Non-deleted violations:', nonDeletedViolations.length);
        console.log('Deleted violations:', deletedViolations.length);
        console.log('');

        console.log('=== NON-DELETED VIOLATIONS ===');
        console.log(JSON.stringify(nonDeletedViolations, null, 2));
        console.log('');

        console.log('=== DELETED VIOLATIONS ===');
        console.log(JSON.stringify(deletedViolations, null, 2));
        console.log('');

        console.log('=== ALL VIOLATIONS (COMBINED) ===');
        console.log(JSON.stringify(allAnnotationData, null, 2));
        console.log('=== END VIOLATION DATA ===');

        // Also log in a format that's easy to copy
        console.log('Easy copy format (all violations):');
        console.log(JSON.stringify(allAnnotationData));

        return allAnnotationData;
    };

    // Use violation categories (already defined above from guideline or default)
    const violationCategoryIds = VIOLATION_CATEGORIES.map(cat => cat.id);

    // Get the DOM element that wraps the image and stage for size calculations
    const getImageContainerElement = () => imageContainerRef.current;

    // Load and render the image
    const loadImageDocument = useCallback(async (url?: string) => {
        const finalImageUrl = url || imageUrl;

        if (!finalImageUrl) {
            console.error('No image URL provided');
            setImageError('画像URLが指定されていません');
            setImageLoading(false);
            return;
        }

        // Enhanced duplicate prevention - check if we're already loading or have loaded the same URL
        if (loadingRef.current && currentImageUrl === finalImageUrl) {
            console.log('Image already loading:', finalImageUrl);
            return;
        }

        // If we already have this image loaded, don't reload it
        if (imageLoaded && currentImageUrl === finalImageUrl && imageElement) {
            console.log('Image already loaded:', finalImageUrl);
            return;
        }

        // If we've already attempted to load this URL, don't try again
        if (attemptedLoadRef.current === finalImageUrl) {
            console.log('Image load already attempted:', finalImageUrl);
            return;
        }

        console.log('Loading image from:', finalImageUrl);
        console.log('Image URL details:', {
            finalImageUrl,
            imageUrl,
            jobId,
            urlId,
            currentImageUrl,
            imageLoaded,
            loadingRef: loadingRef.current
        });

        loadingRef.current = true;
        attemptedLoadRef.current = finalImageUrl;
        setCurrentImageUrl(finalImageUrl);
        setImageLoading(true);
        setImageError(null);

        // Clean up previous blob URL if it exists
        if (imageBlobUrlRef.current) {
            URL.revokeObjectURL(imageBlobUrlRef.current);
            imageBlobUrlRef.current = null;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS for image loading

        // Add loading progress tracking for large images
        let loadingStartTime = Date.now();
        console.log('Starting image load at:', new Date().toISOString());

            try {
            // Check if this is a PNG endpoint that requires authentication
            const isPngEndpoint = finalImageUrl.includes('/pngs/');
            
            let imageSrc = finalImageUrl;
            
            if (isPngEndpoint) {
                // Extract the endpoint path
                // Note: apiClient.baseURL already includes /api/v1, so endpoint should be relative to that
                let endpoint: string;
                
                if (finalImageUrl.startsWith('http')) {
                    // Full URL - extract pathname
                    const urlObj = new URL(finalImageUrl);
                    let pathname = urlObj.pathname;
                    
                    // Remove /api/v1 prefix if present (since apiClient.baseURL already includes it)
                    if (pathname.startsWith('/api/v1/')) {
                        pathname = pathname.substring(7); // Remove '/api/v1'
                    } else if (pathname.startsWith('/api/v1')) {
                        pathname = pathname.substring(7); // Remove '/api/v1'
                    }
                    
                    endpoint = pathname + urlObj.search;
                } else {
                    // Relative URL
                    endpoint = finalImageUrl.startsWith('/') ? finalImageUrl : `/${finalImageUrl}`;
                    
                    // Remove /api/v1 prefix if present (since apiClient.baseURL already includes it)
                    if (endpoint.startsWith('/api/v1/')) {
                        endpoint = endpoint.substring(7); // Remove '/api/v1'
                    } else if (endpoint.startsWith('/api/v1')) {
                        endpoint = endpoint.substring(7); // Remove '/api/v1'
                    }
                }
                
                // Ensure endpoint starts with /pngs/ (without /api/v1 prefix)
                // The endpoint should be like "/pngs/job_xxx/yyy.png"
                if (!endpoint.startsWith('/pngs/')) {
                    // Try to find /pngs/ in the path
                    const pngsIndex = endpoint.indexOf('/pngs/');
                    if (pngsIndex !== -1) {
                        endpoint = endpoint.substring(pngsIndex);
                    } else {
                        // If no /pngs/ found, this might be a malformed URL
                        console.warn('PNG endpoint does not contain /pngs/:', endpoint);
                    }
                }
                
                // Fetch image as blob with authentication
                console.log('Fetching authenticated PNG with endpoint:', endpoint);
                const blob = await apiClient.getImageBlob(endpoint);
                const blobUrl = URL.createObjectURL(blob);
                imageBlobUrlRef.current = blobUrl;
                imageSrc = blobUrl;
                console.log('Image fetched as blob, created object URL');
            }

            img.onload = () => {
                const loadingTime = Date.now() - loadingStartTime;
                console.log('Image loaded successfully, dimensions:', img.width, 'x', img.height);
                console.log('Image loading took:', loadingTime, 'ms');
                loadingRef.current = false;
                setImageLoading(false);
                setImageError(null);
                setImageLoaded(true);
                setImageElement(img);

                // Calculate display dimensions based on the actual canvas area width (not the sidebar).
                // This lets the violation canvas expand on wide screens without changing sidebar width.
                const canvasAreaWidth =
                    scrollContainerRef.current?.clientWidth
                    || stageContainerRef.current?.clientWidth
                    || 0;

                // Fallback: viewport width minus left sidebar (320px) and some outer padding.
                const fallbackWidth = Math.max(400, window.innerWidth - sidebarWidthPx - 80);
                const finalContainerWidth = canvasAreaWidth > 0 ? canvasAreaWidth : fallbackWidth;

                const padding = 24;
                const availableWidth = Math.max(finalContainerWidth - padding, 400);

                // Calculate scale to fit width
                const scale = availableWidth / img.width;
                const displayWidth = img.width * scale;
                const displayHeight = img.height * scale;

                setImageDimensions({
                    width: displayWidth,
                    height: displayHeight
                });

                setImageRendered(true);
                console.log('[ViolationCanvas] Image rendered successfully:', {
                    naturalSize: { width: img.width, height: img.height },
                    displaySize: { width: displayWidth, height: displayHeight },
                    scale: displayWidth / img.width,
                    imageLoaded: true,
                    imageLoading: false,
                    imageRendered: true
                });
            };

            img.onerror = (error) => {
                console.error('Error loading image:', error);
                console.error('Image URL that failed:', finalImageUrl);
                console.error('Image element:', img);
                if (error instanceof Event) {
                    console.error('Error details:', {
                        type: error.type,
                        target: error.target,
                        currentTarget: error.currentTarget
                    });
                } else {
                    console.error('Error details:', error);
                }
                loadingRef.current = false;
                attemptedLoadRef.current = null; // Clear so we can retry if needed
                setImageError(`画像の読み込みに失敗しました: ${finalImageUrl}（詳細はコンソールを確認してください）`);
                setImageLoading(false);
                setImageLoaded(false);
                
                // Clean up blob URL on error
                if (imageBlobUrlRef.current) {
                    URL.revokeObjectURL(imageBlobUrlRef.current);
                    imageBlobUrlRef.current = null;
                }
            };

            img.src = imageSrc;
        } catch (error) {
            console.error('Error fetching image:', error);
            loadingRef.current = false;
            attemptedLoadRef.current = null;
            setImageError(`画像の取得に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
            setImageLoading(false);
            setImageLoaded(false);
            
            // Clean up blob URL on error
            if (imageBlobUrlRef.current) {
                URL.revokeObjectURL(imageBlobUrlRef.current);
                imageBlobUrlRef.current = null;
            }
        }
    }, [imageUrl, currentImageUrl, imageLoaded, imageElement]);

    // Generate image URL from jobId and urlId
    const generateImageUrl = useCallback(() => {
        // If we already have a full imageUrl, use it
        if (imageUrl && imageUrl.startsWith('http')) {
            return imageUrl;
        }
        // Otherwise, construct from jobId and urlId
        if (jobId && urlId) {
            return `/api/v1/pngs/job_${jobId}/${urlId}.png`;
        }
        return imageUrl;
    }, [jobId, urlId, imageUrl]);

    const recalcDisplayDimensions = useCallback(() => {
        const img = imageElement;
        if (!img) return;
        if (!img.width || !img.height) return;

        const canvasAreaWidth =
            scrollContainerRef.current?.clientWidth
            || stageContainerRef.current?.clientWidth
            || 0;

        const fallbackWidth = Math.max(400, window.innerWidth - sidebarWidthPx - 80);
        const finalContainerWidth = canvasAreaWidth > 0 ? canvasAreaWidth : fallbackWidth;

        const padding = 24;
        const availableWidth = Math.max(finalContainerWidth - padding, 400);

        const scale = availableWidth / img.width;
        const displayWidth = img.width * scale;
        const displayHeight = img.height * scale;

        setImageDimensions((prev) => {
            if (Math.abs(prev.width - displayWidth) < 1 && Math.abs(prev.height - displayHeight) < 1) {
                return prev;
            }
            return { width: displayWidth, height: displayHeight };
        });
    }, [imageElement, sidebarWidthPx]);

    useEffect(() => {
        console.log('ViolationCanvas component mounted');
        console.log('Job ID:', jobId, 'URL ID:', urlId);

        // Load violations if available and they've changed
        if (violations.length > 0) {
            // Check if violations have actually changed to prevent unnecessary re-processing
            const violationsChanged = JSON.stringify(violations) !== JSON.stringify(previousViolationsRef.current);

            if (violationsChanged) {
                console.log('Loading violation annotations:', violations);
                console.log('Violation IDs:', violations.map(v => v.id));

                // Check for duplicate IDs in the input violations
                const violationIds = violations.map(v => v.id);
                const uniqueIds = new Set(violationIds);
                if (violationIds.length !== uniqueIds.size) {
                    console.warn(`Found ${violationIds.length - uniqueIds.size} duplicate violation IDs in input data:`, violationIds);
                }

                const violationAnnotations = ensureUIStateFields(violations);

                // Deduplicate annotations by ID to prevent duplicate keys
                const annotationMap = new Map<string, ViolationData>();
                violationAnnotations.forEach(annotation => {
                    if (!annotationMap.has(annotation.id)) {
                        annotationMap.set(annotation.id, annotation);
                    }
                });
                const uniqueAnnotations = Array.from(annotationMap.values());

                console.log(`Converted ${violations.length} violations to ${uniqueAnnotations.length} unique annotations`);
                setAnnotations(uniqueAnnotations);

                // Update the ref to track current violations
                previousViolationsRef.current = violations;
            } else {
                console.log('Violations unchanged, skipping re-processing');
            }
        }
    }, [violations]);

    useEffect(() => {
        if (!initialSelectedViolationId) return;
        if (!annotations || annotations.length === 0) return;
        if (appliedInitialViolationIdRef.current === initialSelectedViolationId) return;

        const exists = annotations.some((a) => a.id === initialSelectedViolationId);
        if (!exists) return;

        appliedInitialViolationIdRef.current = initialSelectedViolationId;
        console.log('[ViolationCanvas] apply initial violationId from URL', {
            initialSelectedViolationId,
        });
        setSelectedAnnotationId(initialSelectedViolationId);
        setAnnotations((prev) =>
            prev.map((a) => ({
                ...a,
                isSelected: a.id === initialSelectedViolationId,
            }))
        );
    }, [annotations, initialSelectedViolationId]);

    // Recalculate canvas scale when the drawer opens/closes (and on container resize).
    useEffect(() => {
        if (!imageLoaded || !imageElement) return;

        // Drawer toggle changes available width, so rescale to fit remaining space.
        recalcDisplayDimensions();

        const container = scrollContainerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver(() => {
            recalcDisplayDimensions();
        });
        ro.observe(container);
        return () => ro.disconnect();
    }, [imageLoaded, imageElement, sidebarWidthPx, recalcDisplayDimensions]);

    // Reset previous violations ref when jobId or urlId changes
    useEffect(() => {
        previousViolationsRef.current = [];
    }, [jobId, urlId]);

    // Separate useEffect for image loading
    useEffect(() => {
        // Load image if we have jobId and urlId, and we're not already loading/loaded
        if (jobId && urlId && !loadingRef.current && !imageLoaded) {
            const generatedImageUrl = generateImageUrl();
            if (generatedImageUrl && generatedImageUrl !== currentImageUrl) {
                console.log('Loading image with jobId and urlId:', generatedImageUrl);
                loadImageDocument(generatedImageUrl);
            }
        }
    }, [jobId, urlId, generateImageUrl, currentImageUrl, loadImageDocument, imageLoaded]);

    // Load image when imageUrl changes
    useEffect(() => {
        if (imageUrl && imageUrl !== currentImageUrl && !loadingRef.current && !imageLoaded) {
            console.log('Loading image from imageUrl prop:', imageUrl);
            loadImageDocument(imageUrl);
        }
    }, [imageUrl, currentImageUrl, loadImageDocument, imageLoaded]);


    // Add timeout for image loading
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (imageLoading) {
                console.log('Image loading timeout - setting fallback state');
                setImageLoading(false);
                setImageError('Image loading timeout - using fallback dimensions');
                setImageLoaded(true);
                setImageDimensions({ width: 800, height: 1000 });
            }
        }, 30000); // 30 second timeout for large images

        return () => clearTimeout(timeout);
    }, [imageLoading]);

    // Handle window resize - only update dimensions, don't re-render image
    useEffect(() => {
        const handleResize = () => {
            if (imageRendered && !imageLoading) {
                // Only update the Stage dimensions, don't re-render the image
                if (stageRef.current) {
                    stageRef.current.batchDraw();
                }
                console.log('Window resized, Stage redrawn');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [imageRendered, imageLoading]);

    // Force Stage to update when image dimensions change
    useEffect(() => {
        if (stageRef.current && imageRendered) {
            // Force the Stage to redraw with new dimensions
            stageRef.current.batchDraw();
        }
    }, [imageDimensions, imageRendered]);

    // Close category dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
                setShowCategoryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);





    const getMousePosition = (e: any) => {
        const stage = stageRef.current;
        if (!stage) return null;

        // Viewport-based Stage:
        // - Stage is only (viewport + padding) in size.
        // - As the user scrolls, we shift what part of the "world" the Stage represents using dx/dy.
        // Therefore Konva's pointer position is *viewport-local*; we must convert it to *world* coords.
        const pointer = stage.getPointerPosition();
        if (!pointer) return null;

        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return { x: pointer.x, y: pointer.y };

        const PADDING = 500;
        const dx = scrollContainer.scrollLeft - PADDING;
        const dy = scrollContainer.scrollTop - PADDING;

        // Convert viewport-local pointer -> world coordinates (same coordinate space used by annotations)
        return { x: pointer.x + dx, y: pointer.y + dy };
    };

    const handleMouseDown = (e: any) => {
        if (isReadOnly) return;
        const clickedOnEmpty = e.target === e.target.getStage();
        if (!clickedOnEmpty) {
            return;
        }

        // Prevent text selection when drawing
        e.evt.preventDefault();
        e.evt.stopPropagation();

        // Unselect any selected annotation when clicking on empty area
        if (selectedAnnotationId) {
            setSelectedAnnotationId(null);
            setAnnotations(prev => prev.map(annotation => ({
                ...annotation,
                isSelected: false
            })));
        }

        const pos = getMousePosition(e);
        if (pos) {
            // Only allow drawing on the image area (not in header area)
            if (pos.y < headerHeight) {
                console.log('Click in header area, not starting draw');
                return;
            }
            
            console.log('Starting to draw at:', {
                pointer: pos,
                stagePosition,
                headerHeight,
                imageRelativeY: pos.y - headerHeight,
                stagePointer: stageRef.current?.getPointerPosition()
            });
            setIsDrawing(true);
            setStartPoint(pos);
            setCurrentAnnotation({ x: pos.x, y: pos.y, width: 0, height: 0 });
        }
    };

    const handleMouseMove = (e: any) => {
        if (isResizing) {
            handleResizeMove(e);
            return;
        }

        if (!isDrawing || !startPoint) return;

        // Prevent text selection during drawing
        e.evt.preventDefault();

        const pos = getMousePosition(e);
        if (pos) {
            const width = Math.abs(pos.x - startPoint.x);
            const height = Math.abs(pos.y - startPoint.y);
            const x = Math.min(pos.x, startPoint.x);
            const y = Math.min(pos.y, startPoint.y);

            setCurrentAnnotation({ x, y, width, height });
        }
    };

    const handleMouseUp = (e?: any) => {
        if (isResizing) {
            handleResizeEnd();
            return;
        }

        if (!isDrawing || !startPoint || !currentAnnotation) return;

        // Prevent text selection when finishing drawing
        if (e) {
            e.evt?.preventDefault();
            e.evt?.stopPropagation();
        }

        if (currentAnnotation.width > 10 && currentAnnotation.height > 10) {
            // Account for headerHeight offset when converting to ratios
            // Mouse coordinates are in Stage container space (includes headerHeight area at top)
            // yRatio should be relative to image only (image starts at headerHeight)
            const imageY = Math.max(0, currentAnnotation.y - headerHeight);
            const imageYEnd = Math.max(0, (currentAnnotation.y + currentAnnotation.height) - headerHeight);
            const imageHeight = Math.max(0, imageYEnd - imageY);
            
            console.log('Creating violation with coordinates:', {
                stageCoordinates: { x: currentAnnotation.x, y: currentAnnotation.y, width: currentAnnotation.width, height: currentAnnotation.height },
                imageCoordinates: { x: currentAnnotation.x, y: imageY, width: currentAnnotation.width, height: imageHeight },
                headerHeight,
                imageDimensions,
                ratios: {
                    xRatio: pixelsToRatio(currentAnnotation.x, imageDimensions.width),
                    yRatio: pixelsToRatio(imageY, imageDimensions.height),
                    widthRatio: pixelsToRatio(currentAnnotation.width, imageDimensions.width),
                    heightRatio: pixelsToRatio(imageHeight, imageDimensions.height)
                }
            });
            
            const newAnnotation: ViolationData = {
                id: generateId(),
                category: '',
                severity: 'medium',
                violationType: 'manual_created',
                scope: 'specific_content',
                violationContent: '',
                editNote: '',
                xRatio: pixelsToRatio(currentAnnotation.x, imageDimensions.width),
                yRatio: pixelsToRatio(imageY, imageDimensions.height),
                widthRatio: pixelsToRatio(currentAnnotation.width, imageDimensions.width),
                heightRatio: pixelsToRatio(imageHeight, imageDimensions.height),
                confidence: 0,
                createdAt: new Date().toISOString(),
                createdBy: 'user',
                isSelected: false,
                deleted: false,
                categoryContentExistence: true,
                categoryContentViolationExistence: true,
            };

            console.log('=== NEW VIOLATION CREATED IN VIOLATIONCANVAS ===');
            console.log('New annotation ID:', newAnnotation.id);
            console.log('=== END NEW VIOLATION CREATED ===');

            // Add the annotation to state first
            updateAnnotations(prev => {
                const updatedAnnotations = [...prev, newAnnotation];

                // Log the new annotation data for easy copying
                console.log('=== NEW ANNOTATION CREATED ===');
                console.log('Annotation ID:', newAnnotation.id);
                console.log('Position (ratios):', {
                    xRatio: newAnnotation.xRatio,
                    yRatio: newAnnotation.yRatio,
                    widthRatio: newAnnotation.widthRatio,
                    heightRatio: newAnnotation.heightRatio
                });
                console.log('Position (pixels):', {
                    x: currentAnnotation.x,
                    y: currentAnnotation.y,
                    width: currentAnnotation.width,
                    height: currentAnnotation.height
                });
                console.log('=== END NEW ANNOTATION ===');

                return updatedAnnotations;
            });
            setSelectedAnnotationId(newAnnotation.id);

            // Clear drawing state immediately
            setIsDrawing(false);
            setStartPoint(null);

            // Keep currentAnnotation visible for a moment before clearing it
            setTimeout(() => {
                setCurrentAnnotation(null);
            }, 50);

            // Open the popup after a small delay to ensure the annotation is rendered
            setTimeout(() => {
                setEditingAnnotationId(newAnnotation.id);
                setEditText('');
            }, 150);
        } else {
            // Clear drawing state if annotation is too small
            setIsDrawing(false);
            setStartPoint(null);
            setCurrentAnnotation(null);
        }
    };

    const handleAnnotationClick = (annotationId: string) => {
        // Find the annotation and check if it's deleted
        const annotation = annotations.find(a => a.id === annotationId);
        console.log('[ViolationCanvas] select click', {
            annotationId,
            found: Boolean(annotation),
            deleted: Boolean(annotation?.deleted),
            allowDeletedInView,
            passesCanvasVisibility: annotation ? passesCanvasVisibility(annotation) : false,
        });
        if (!annotation || (!allowDeletedInView && annotation.deleted)) {
            console.warn('Cannot select deleted or non-existent annotation:', annotationId);
            return;
        }

        setSelectedAnnotationId(annotationId);
        setAnnotations(prev => prev.map(annotation => ({
            ...annotation,
            isSelected: annotation.id === annotationId,
        })));

        // Scroll to the annotation position with a small delay to ensure state updates
        if (annotation) {
            if (pendingScrollTimeoutRef.current !== null) {
                clearTimeout(pendingScrollTimeoutRef.current);
            }
            pendingScrollTimeoutRef.current = window.setTimeout(() => {
                pendingScrollTimeoutRef.current = null;
                if (selectedAnnotationIdRef.current !== annotationId) {
                    console.log('[ViolationCanvas] skip scroll: selection changed', {
                        expected: annotationId,
                        actual: selectedAnnotationIdRef.current,
                    });
                    return;
                }
                console.log('[ViolationCanvas] scroll to selected', {
                    annotationId,
                    deleted: Boolean(annotation.deleted),
                    hasPosition: hasPosition(annotation),
                });
                scrollToAnnotation(annotation);
                // Also scroll to the selected violation in the list
                scrollToSelectedViolation(annotationId);
            }, 100);
        }
    };

    // Auto-select the first visible violation when opening the page (or when filters hide the current selection).
    useEffect(() => {
        if (selectedAnnotationId) return;
        if (didAutoSelectRef.current) return;
        const firstVisible = sortedVisibleAnnotations[0];
        if (firstVisible) {
            didAutoSelectRef.current = true;
            // Use the same selection path as a user click so that scrolling and zoom panel behavior stays consistent.
            handleAnnotationClick(firstVisible.id);
        }
    }, [sortedVisibleAnnotations, selectedAnnotationId]);

    // Function to select next annotation in tab order
    const selectNextAnnotation = useCallback(() => {
        // Filter out deleted annotations
        const visibleAnnotations = annotations.filter(passesCanvasVisibility);
        if (visibleAnnotations.length === 0) return;

        // Sort visible annotations by center point (top to bottom, left to right)
        const sortedAnnotations = [...visibleAnnotations].sort((a, b) => {
            // Handle unpositioned violations - put them at the beginning
            const aHasPosition = hasPosition(a);
            const bHasPosition = hasPosition(b);

            if (!aHasPosition && !bHasPosition) {
                // Both unpositioned: same order as the violation list / canvas band —
                // overall-page first, then off-capture, preserving original order.
                const aOff = isOffCaptureScope(a) ? 1 : 0;
                const bOff = isOffCaptureScope(b) ? 1 : 0;
                if (aOff !== bOff) return aOff - bOff;
                return 0;
            }
            if (!aHasPosition) return -1; // a goes before b
            if (!bHasPosition) return 1; // b goes before a

            // Both have positions - calculate center points
            const aCenterY = ratioToPixels((a.yRatio || 0) + (a.heightRatio || 0) / 2, imageDimensions.height);
            const bCenterY = ratioToPixels((b.yRatio || 0) + (b.heightRatio || 0) / 2, imageDimensions.height);

            // First sort by Y center position (top to bottom)
            const yDiff = aCenterY - bCenterY;
            if (Math.abs(yDiff) > 5) { // If Y difference is significant (>5px), sort by Y
                return yDiff;
            }

            // If Y positions are similar (same line), sort by X center position (left to right)
            const aCenterX = ratioToPixels((a.xRatio || 0) + (a.widthRatio || 0) / 2, imageDimensions.width);
            const bCenterX = ratioToPixels((b.xRatio || 0) + (b.widthRatio || 0) / 2, imageDimensions.width);
            return aCenterX - bCenterX;
        });

        if (!selectedAnnotationId) {
            // If no annotation is selected, select the first visible annotation
            const firstAnnotation = sortedAnnotations[0];
            handleAnnotationClick(firstAnnotation.id);
        } else {
            // Find current annotation index and select next one
            const currentIndex = sortedAnnotations.findIndex(annotation => annotation.id === selectedAnnotationId);
            const nextIndex = (currentIndex + 1) % sortedAnnotations.length;
            const nextAnnotation = sortedAnnotations[nextIndex];
            handleAnnotationClick(nextAnnotation.id);
        }
    }, [annotations, selectedAnnotationId, imageDimensions, handleAnnotationClick, passesCanvasVisibility]);


    // Function to scroll to selected violation in the list
    const scrollToSelectedViolation = (annotationId: string) => {
        if (!violationListRef.current) return;

        // Find the violation item element
        const violationItem = violationListRef.current.querySelector(`[data-violation-id="${annotationId}"]`);
        if (violationItem) {
            // Scroll the violation into view
            violationItem.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }
    };

    const scrollToAnnotation = (annotation: ViolationData) => {
        // Find the scrollable container - try multiple approaches
        let scrollableContainer = null;

        // First try: look for the canvas container
        const canvasContainer = document.getElementById('canvas-container');
        if (canvasContainer && canvasContainer.scrollHeight > canvasContainer.clientHeight) {
            scrollableContainer = canvasContainer;
        }

        // Second try: look for the main content container
        if (!scrollableContainer) {
            const mainContent = document.querySelector('.main-content');
            if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
                scrollableContainer = mainContent;
            }
        }

        // Third try: look for the page content container
        if (!scrollableContainer) {
            const pageContent = document.querySelector('.page-content');
            if (pageContent && pageContent.scrollHeight > pageContent.clientHeight) {
                scrollableContainer = pageContent;
            }
        }

        // Fourth try: use the image container's parent hierarchy
        if (!scrollableContainer) {
            let current = imageContainerRef.current?.parentElement;
            while (current && current !== document.body) {
                if (current.scrollHeight > current.clientHeight) {
                    scrollableContainer = current;
                    break;
                }
                current = current.parentElement;
            }
        }

        if (scrollableContainer) {
            // Calculate the absolute scroll position to center the annotation
            const containerRect = scrollableContainer.getBoundingClientRect();
            let targetCenterY = 0;

            if (hasPosition(annotation)) {
                // Positioned item: include header offset
                const y = ratioToPixels(annotation.yRatio || 0, imageDimensions.height);
                const h = ratioToPixels(annotation.heightRatio || 0, imageDimensions.height);
                targetCenterY = headerHeight + y + (h / 2);
            } else {
                // Unpositioned item: scroll to its header row center
                const row = measuredUnpositioned.find(r => r.id === annotation.id);
                if (row) {
                    targetCenterY = row.boxY + (row.boxH / 2);
                } else {
                    targetCenterY = headerHeight / 2; // fallback
                }
            }

            // Calculate the absolute scroll position needed to center the target
            const absoluteScrollTop = targetCenterY - (containerRect.height / 2);

            // Add some padding to ensure the annotation is fully visible
            const padding = 50;
            const finalScrollTop = Math.max(0, absoluteScrollTop - padding);

            console.log('Scrolling to annotation:', {
                targetCenterY,
                containerHeight: containerRect.height,
                currentScrollTop: scrollableContainer.scrollTop,
                absoluteScrollTop,
                finalScrollTop,
                annotationPosition: { x: ratioToPixels(annotation.xRatio || 0, imageDimensions.width), y: ratioToPixels(annotation.yRatio || 0, imageDimensions.height) },
                scrollableContainer: scrollableContainer.className || scrollableContainer.tagName
            });

            // Smooth scroll to the annotation position
            scrollableContainer.scrollTo({
                top: finalScrollTop,
                behavior: 'smooth'
            });

            // Update zoomed view position after scroll animation completes
            // Smooth scroll typically takes ~500ms, so we wait a bit longer
            if (pendingScrollUpdateTimeoutRef.current !== null) {
                clearTimeout(pendingScrollUpdateTimeoutRef.current);
            }
            pendingScrollUpdateTimeoutRef.current = window.setTimeout(() => {
                pendingScrollUpdateTimeoutRef.current = null;
                // Temporarily skip post-scroll update for testing
                // if (!imageElement || !showZoomedView) {
                //     return;
                // }
                // if (selectedAnnotationIdRef.current !== annotation.id) {
                //     return;
                // }
                // const latestAnnotation = annotationsRef.current.find(a => a.id === annotation.id);
                // if (latestAnnotation && !latestAnnotation.deleted && hasPosition(latestAnnotation)) {
                //     // Clear processing ref to allow position update after scroll
                //     processingAnnotationIdRef.current = null;
                //     updateZoomedView(latestAnnotation);
                // }
            }, 600); // Wait for smooth scroll to complete (temporarily disabled update)
        } else {
            console.warn('No scrollable container found for scrolling to annotation');
        }
    };

    // Function to delete selected annotation (soft delete)
    const deleteSelectedAnnotation = useCallback(() => {
        if (isReadOnly) {
            toast.warning('この画面は閲覧専用です。', { title: '閲覧専用' });
            return;
        }
        if (!selectedAnnotationId) {
            toast.warning('Please select an annotation to delete first.', { title: 'No selection' });
            return;
        }

        // Find the annotation and check if it's already deleted
        const annotationToDelete = annotations.find(annotation => annotation.id === selectedAnnotationId);
        if (!annotationToDelete) {
            console.warn('Cannot find annotation to delete:', selectedAnnotationId);
            return;
        }

        if (annotationToDelete.deleted) {
            console.warn('Annotation is already deleted:', selectedAnnotationId);
            return;
        }

        updateAnnotations(prev => {
            // Store the deleted annotation for undo functionality
            setLastDeletedAnnotation(annotationToDelete);

            // Mark the annotation as deleted instead of removing it
            const updatedAnnotations = prev.map(annotation =>
                annotation.id === selectedAnnotationId
                    ? { ...annotation, deleted: true, isSelected: false }
                    : annotation
            );

            // Notify parent with updated annotations (deleted violations are included in the main array)
            notifyViolationsChange(updatedAnnotations);

            // Get visible annotations for auto-selection (respect sidebar filters)
            const visibleAnnotations = updatedAnnotations.filter(passesCanvasVisibility);

            // Auto-select next annotation if visible annotations remain
            if (visibleAnnotations.length > 0) {
                // Sort visible annotations by center point (top to bottom, left to right) to find next annotation
                const sortedAnnotations = [...visibleAnnotations].sort((a, b) => {
                    // Handle unpositioned violations - put them at the beginning
                    const aHasPosition = hasPosition(a);
                    const bHasPosition = hasPosition(b);

                    if (!aHasPosition && !bHasPosition) {
                        return a.id.localeCompare(b.id);
                    }
                    if (!aHasPosition) return -1; // a goes before b
                    if (!bHasPosition) return 1; // b goes before a

                    // Both have positions - calculate center points
                    const aCenterY = ratioToPixels((a.yRatio || 0) + (a.heightRatio || 0) / 2, imageDimensions.height);
                    const bCenterY = ratioToPixels((b.yRatio || 0) + (b.heightRatio || 0) / 2, imageDimensions.height);

                    // First sort by Y center position (top to bottom)
                    const yDiff = aCenterY - bCenterY;
                    if (Math.abs(yDiff) > 5) { // If Y difference is significant (>5px), sort by Y
                        return yDiff;
                    }

                    // If Y positions are similar (same line), sort by X center position (left to right)
                    const aCenterX = ratioToPixels((a.xRatio || 0) + (a.widthRatio || 0) / 2, imageDimensions.width);
                    const bCenterX = ratioToPixels((b.xRatio || 0) + (b.widthRatio || 0) / 2, imageDimensions.width);
                    return aCenterX - bCenterX;
                });

                // Find the index of the deleted annotation in the original sorted list of visible annotations
                const originalVisibleAnnotations = prev.filter(passesCanvasVisibility);
                const originalSortedAnnotations = [...originalVisibleAnnotations].sort((a, b) => {
                    // Handle unpositioned violations - put them at the beginning
                    const aHasPosition = hasPosition(a);
                    const bHasPosition = hasPosition(b);

                    if (!aHasPosition && !bHasPosition) {
                        return a.id.localeCompare(b.id);
                    }
                    if (!aHasPosition) return -1; // a goes before b
                    if (!bHasPosition) return 1; // b goes before a

                    // Both have positions - calculate center points
                    const aCenterY = ratioToPixels((a.yRatio || 0) + (a.heightRatio || 0) / 2, imageDimensions.height);
                    const bCenterY = ratioToPixels((b.yRatio || 0) + (b.heightRatio || 0) / 2, imageDimensions.height);

                    const yDiff = aCenterY - bCenterY;
                    if (Math.abs(yDiff) > 5) {
                        return yDiff;
                    }

                    const aCenterX = ratioToPixels((a.xRatio || 0) + (a.widthRatio || 0) / 2, imageDimensions.width);
                    const bCenterX = ratioToPixels((b.xRatio || 0) + (b.widthRatio || 0) / 2, imageDimensions.width);
                    return aCenterX - bCenterX;
                });

                const deletedAnnotationIndex = originalSortedAnnotations.findIndex(annotation => annotation.id === selectedAnnotationId);

                // Select the next annotation in the sequence, or the first annotation if we deleted the last one
                let nextAnnotationIndex = deletedAnnotationIndex;
                if (nextAnnotationIndex >= sortedAnnotations.length) {
                    nextAnnotationIndex = 0; // Wrap to first annotation if we deleted the last one
                }

                const nextAnnotation = sortedAnnotations[nextAnnotationIndex];

                // Use setTimeout to ensure state updates are processed
                setTimeout(() => {
                    setSelectedAnnotationId(nextAnnotation.id);
                    updateAnnotations(prevBoxes => prevBoxes.map(annotation => ({
                        ...annotation,
                        isSelected: annotation.id === nextAnnotation.id,
                    })));

                    // Scroll to the newly selected annotation
                    setTimeout(() => {
                        scrollToAnnotation(nextAnnotation);
                        // Also scroll to the selected violation in the list
                        scrollToSelectedViolation(nextAnnotation.id);
                    }, 100);
                }, 50);
            } else {
                // No visible annotations remain, clear selection
                setSelectedAnnotationId(null);
            }

            return updatedAnnotations;
        });
    }, [selectedAnnotationId, imageDimensions, scrollToAnnotation, updateAnnotations, toast, annotations, passesCanvasVisibility]);

    // Function to restore the last deleted annotation
    const restoreLastDeletedAnnotation = useCallback(() => {
        if (!lastDeletedAnnotation) return;

        setAnnotations(prev => {
            const updatedAnnotations = prev.map(annotation =>
                annotation.id === lastDeletedAnnotation.id
                    ? { ...lastDeletedAnnotation, deleted: false, isSelected: false }
                    : annotation
            );

            // Select the restored annotation
            setSelectedAnnotationId(lastDeletedAnnotation.id);
            setAnnotations(prevBoxes => prevBoxes.map(annotation => ({
                ...annotation,
                isSelected: annotation.id === lastDeletedAnnotation.id,
            })));

            // Scroll to the restored annotation
            setTimeout(() => {
                scrollToAnnotation(lastDeletedAnnotation);
                // Also scroll to the selected violation in the list
                scrollToSelectedViolation(lastDeletedAnnotation.id);
            }, 100);

            return updatedAnnotations;
        });

        // Clear the last deleted annotation since it's been restored
        setLastDeletedAnnotation(null);
    }, [lastDeletedAnnotation, scrollToAnnotation]);

    const handleAnnotationDoubleClick = (annotationId: string) => {
        const annotation = annotations.find(b => b.id === annotationId);
        if (annotation && (allowDeletedInView || !annotation.deleted)) {
            setEditingAnnotationId(annotationId);
            setEditText(annotation.editNote || '');
            setEditContent(annotation.scope === 'overall_page' ? '' : (annotation.violationContent || ''));
            setEditCaptureUrl(annotation.scope === 'off_capture' ? (annotation.captureUrl || '') : '');
            setCaptureUrlPreviewError(false);
            setSelectedCategory(annotation.category);
        } else {
            console.warn('Cannot edit deleted or non-existent annotation:', annotationId);
        }
    };


    // Grows the textarea to fit its content so switching a violation box into
    // edit mode doesn't snap it to a fixed row count and change the box height.
    // Caps out at VIOLATION_NOTE_MAX_HEIGHT_PX (scrolling beyond that) so very
    // long notes don't blow up the box height either.
    const resizeTextareaToContent = (el: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = 'auto';
        const contentHeight = el.scrollHeight;
        const cappedHeight = Math.min(contentHeight, VIOLATION_NOTE_MAX_HEIGHT_PX);
        el.style.height = `${cappedHeight}px`;
        el.style.overflowY = contentHeight > VIOLATION_NOTE_MAX_HEIGHT_PX ? 'auto' : 'hidden';
    };

    const handleTextEdit = (annotationId: string, newText: string) => {
        updateAnnotations(prev => prev.map(annotation =>
            annotation.id === annotationId ? { ...annotation, editNote: newText } : annotation
        ));
    };

    const handleViolationContentEdit = (annotationId: string, newContent: string) => {
        updateAnnotations(prev => prev.map(annotation =>
            annotation.id === annotationId ? { ...annotation, violationContent: newContent } : annotation
        ));
    };

    const handleCategoryEdit = (annotationId: string, newCategory: string) => {
        updateAnnotations(prev => prev.map(annotation =>
            annotation.id === annotationId ? { ...annotation, category: newCategory } : annotation
        ));
    };

    const handleCaptureUrlEdit = (annotationId: string, newCaptureUrl: string) => {
        updateAnnotations(prev => prev.map(annotation =>
            annotation.id === annotationId ? { ...annotation, captureUrl: newCaptureUrl } : annotation
        ));
    };

    // Filter annotations based on search term and category (exclude deleted annotations)
    const getFilteredBoxes = () => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return annotations.filter(annotation => {
            if (!passesCanvasVisibility(annotation)) return false;

            const categoryTitle = getCategoryTitle(annotation.category || '');

            const matchesSearch = !normalizedSearch ||
                (annotation.violationContent && annotation.violationContent.toLowerCase().includes(normalizedSearch)) ||
                (annotation.editNote && annotation.editNote.toLowerCase().includes(normalizedSearch)) ||
                (categoryTitle && categoryTitle.toLowerCase().includes(normalizedSearch));

            return matchesSearch;
        });
    };

    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('');
    };


    // Highlight search terms in text
    const highlightSearchTerm = (text: string, searchTerm: string) => {
        if (!searchTerm) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) => {
            if (regex.test(part)) {
                return (
                    <span key={index} style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: '600',
                        padding: '1px 2px',
                        borderRadius: '2px'
                    }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const handleTextEditComplete = () => {
        if (editingAnnotationId) {
            handleTextEdit(editingAnnotationId, editText);
            // Overall-page violations must not have violationContent.
            const ann = annotations.find(a => a.id === editingAnnotationId);
            const scope = ann?.scope;
            handleViolationContentEdit(
                editingAnnotationId,
                scope === 'overall_page' ? '' : editContent
            );
            // Capture URL only applies to off-capture violations.
            handleCaptureUrlEdit(
                editingAnnotationId,
                scope === 'off_capture' ? editCaptureUrl.trim() : ''
            );
            if (selectedCategory) {
                handleCategoryEdit(editingAnnotationId, selectedCategory);
            }
            setEditingAnnotationId(null);
            setEditText('');
            setEditContent('');
            setEditCaptureUrl('');
            setSelectedCategory('');
        }
    };

    const handleTextEditCancel = () => {
        setEditingAnnotationId(null);
        setEditText('');
        setEditContent('');
        setEditCaptureUrl('');
        setSelectedCategory('');
    };

    // Handle keyboard events for deleting selected annotations, tab navigation, and enter key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const activeElement = document.activeElement as HTMLElement;
            const isEditingText = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true'
            );

            // Handle popup-specific keyboard shortcuts when edit popup is open
            if (editingAnnotationId) {
                // Handle Shift+Enter to save content
                if (e.key === 'Enter' && e.shiftKey) {
                    e.preventDefault();
                    handleTextEditComplete();
                }

                // Handle Escape key to cancel without saving
                if (e.key === 'Escape') {
                    e.preventDefault();
                    handleTextEditCancel();
                }

                // Handle Enter for new line in textarea (let it pass through naturally)
                if (e.key === 'Enter' && !e.shiftKey && isEditingText && activeElement.tagName === 'TEXTAREA') {
                    // Let the default behavior happen (new line)
                    return;
                }

                // Don't handle other keys when popup is open
                return;
            }

            // Handle main canvas keyboard shortcuts when popup is not open
            // Handle tab navigation
            if (e.key === 'Tab' && !isEditingText) {
                e.preventDefault();
                selectNextAnnotation();
            }

            // Handle enter key to open edit popup
            if (e.key === 'Enter' && selectedAnnotationId && !isEditingText) {
                if (isReadOnly) return;
                e.preventDefault();
                handleAnnotationDoubleClick(selectedAnnotationId);
            }

            // Handle backspace for deletion
            if (e.key === 'Backspace' && selectedAnnotationId && !isEditingText) {
                if (isReadOnly) return;
                e.preventDefault();
                deleteSelectedAnnotation();
            }

            // Handle Ctrl+Z for restoring last deleted annotation
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !isEditingText) {
                if (isReadOnly) return;
                e.preventDefault();
                restoreLastDeletedAnnotation();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isReadOnly, selectedAnnotationId, deleteSelectedAnnotation, editingAnnotationId, selectNextAnnotation, handleAnnotationDoubleClick, handleTextEditComplete, handleTextEditCancel, restoreLastDeletedAnnotation]);



    const handleDragEnd = (annotationId: string, e: any) => {
        if (isReadOnly) return;
        const annotation = annotations.find(a => a.id === annotationId);
        if (!annotation || annotation.deleted) {
            console.warn('Cannot drag deleted or non-existent annotation:', annotationId);
            return;
        }

        // Calculate headerHeight for unpositioned violations (respect sidebar filters)
        const visibleAnnotations = annotations.filter(passesCanvasVisibility);
        const unpositioned = [
            ...visibleAnnotations.filter(isOverallPageScope),
            ...visibleAnnotations.filter(isOffCaptureScope),
        ];
        const headerPadding = unpositioned.length > 0 ? 20 : 0;
        const headerTitleHeight = unpositioned.length > 0 ? 28 : 0;
        const headerRowGap = 10;
        const headerSeparatorGap = unpositioned.length > 0 ? 20 : 0;

        const measuredUnpositioned = (() => {
            if (unpositioned.length === 0) return [];
            const results: Array<{ boxY: number; boxH: number }> = [];
            let cursorY = headerPadding / 2 + headerTitleHeight;
            const boxW = Math.max(0, imageDimensions.width - 20);
            const textWidth = Math.max(60, boxW - 24);
            const titleFontSize = 14;
            const minRowHeight = 70;
            const categoryBlockHeight = 24;

            unpositioned.forEach((ann) => {
                if (isOffCaptureScope(ann)) {
                    const { totalHeight } = buildOffCaptureSegments(ann, textWidth);
                    const boxH = Math.max(minRowHeight, Math.ceil(totalHeight));
                    results.push({ boxY: cursorY, boxH });
                    cursorY += boxH + headerRowGap;
                    return;
                }
                const title = getHeaderRowTitle(ann);
                const wrapped = wrapTextToWidth(String(title), textWidth, titleFontSize, 'sans-serif');
                const titleHeight = Math.max(wrapped.totalHeight, titleFontSize * 1.2);
                const contentHeight = titleHeight + 12 + categoryBlockHeight + 12;
                const boxH = Math.max(minRowHeight, Math.ceil(contentHeight));
                results.push({ boxY: cursorY, boxH });
                cursorY += boxH + headerRowGap;
            });
            return results;
        })();

        const currentHeaderHeight = measuredUnpositioned.length > 0
            ? Math.ceil((measuredUnpositioned[measuredUnpositioned.length - 1].boxY + measuredUnpositioned[measuredUnpositioned.length - 1].boxH) - (headerPadding / 2) + headerPadding / 2 + headerSeparatorGap)
            : 0;

        const newX = e.target.x();
        // Subtract headerHeight from y position since the Group's y includes headerHeight offset
        const newY = e.target.y() - currentHeaderHeight;

        // Use updateAnnotations to notify parent of position changes
        updateAnnotations(prev => prev.map(annotation => {
            if (annotation.id === annotationId) {
                const updatedBox = {
                    ...annotation,
                    xRatio: pixelsToRatio(newX, imageDimensions.width),
                    yRatio: pixelsToRatio(newY, imageDimensions.height)
                };

                // Log the updated position
                console.log('=== BOX MOVED ===');
                console.log('Box ID:', annotationId);
                console.log('New position (ratios):', {
                    xRatio: updatedBox.xRatio,
                    yRatio: updatedBox.yRatio,
                    widthRatio: updatedBox.widthRatio,
                    heightRatio: updatedBox.heightRatio
                });
                console.log('=== END BOX MOVED ===');

                return updatedBox;
            }
            return annotation;
        }));
    };

    // Resize handle functions
    const handleResizeStart = (annotationId: string, handle: string, e: any) => {
        if (isReadOnly) return;
        e.cancelBubble = true;
        e.evt.stopPropagation();

        const annotation = annotations.find(b => b.id === annotationId);
        if (!annotation) return;

        setIsResizing(true);
        setResizeHandle(handle);
        setSelectedAnnotationId(annotationId);

        const pos = getMousePosition(e);
        if (pos) {
            setResizeStartPoint(pos);
            setResizeStartAnnotation({
                x: ratioToPixels(annotation.xRatio || 0, imageDimensions.width),
                y: ratioToPixels(annotation.yRatio || 0, imageDimensions.height),
                width: ratioToPixels(annotation.widthRatio || 0, imageDimensions.width),
                height: ratioToPixels(annotation.heightRatio || 0, imageDimensions.height)
            });
        }
    };

    const handleResizeMove = (e: any) => {
        if (!isResizing || !resizeHandle || !resizeStartPoint || !resizeStartAnnotation || !selectedAnnotationId) return;

        // Check if the selected annotation is deleted
        const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
        if (!selectedAnnotation || selectedAnnotation.deleted) {
            console.warn('Cannot resize deleted or non-existent annotation:', selectedAnnotationId);
            return;
        }

        const pos = getMousePosition(e);
        if (!pos) return;

        const deltaX = pos.x - resizeStartPoint.x;
        // Both pos.y and resizeStartPoint.y are Stage-relative (include headerHeight),
        // but resizeStartAnnotation.y is image-relative. The delta is correct for Stage coordinates,
        // so we can use it directly since we're working with relative changes.
        const deltaY = pos.y - resizeStartPoint.y;

        let newX = resizeStartAnnotation.x;
        let newY = resizeStartAnnotation.y;
        let newWidth = resizeStartAnnotation.width;
        let newHeight = resizeStartAnnotation.height;

        // Calculate new dimensions based on resize handle
        switch (resizeHandle) {
            case 'nw': // Top-left
                newX = resizeStartAnnotation.x + deltaX;
                newY = resizeStartAnnotation.y + deltaY;
                newWidth = resizeStartAnnotation.width - deltaX;
                newHeight = resizeStartAnnotation.height - deltaY;
                break;
            case 'ne': // Top-right
                newY = resizeStartAnnotation.y + deltaY;
                newWidth = resizeStartAnnotation.width + deltaX;
                newHeight = resizeStartAnnotation.height - deltaY;
                break;
            case 'sw': // Bottom-left
                newX = resizeStartAnnotation.x + deltaX;
                newWidth = resizeStartAnnotation.width - deltaX;
                newHeight = resizeStartAnnotation.height + deltaY;
                break;
            case 'se': // Bottom-right
                newWidth = resizeStartAnnotation.width + deltaX;
                newHeight = resizeStartAnnotation.height + deltaY;
                break;
            case 'n': // Top
                newY = resizeStartAnnotation.y + deltaY;
                newHeight = resizeStartAnnotation.height - deltaY;
                break;
            case 's': // Bottom
                newHeight = resizeStartAnnotation.height + deltaY;
                break;
            case 'w': // Left
                newX = resizeStartAnnotation.x + deltaX;
                newWidth = resizeStartAnnotation.width - deltaX;
                break;
            case 'e': // Right
                newWidth = resizeStartAnnotation.width + deltaX;
                break;
        }

        // Ensure minimum size
        const minSize = 20;
        if (newWidth < minSize) {
            if (resizeHandle.includes('w')) {
                newX = resizeStartAnnotation.x + resizeStartAnnotation.width - minSize;
            }
            newWidth = minSize;
        }
        if (newHeight < minSize) {
            if (resizeHandle.includes('n')) {
                newY = resizeStartAnnotation.y + resizeStartAnnotation.height - minSize;
            }
            newHeight = minSize;
        }

        // Update annotation state
        setAnnotations(prev => prev.map(annotation =>
            annotation.id === selectedAnnotationId ? {
                ...annotation,
                xRatio: pixelsToRatio(newX, imageDimensions.width),
                yRatio: pixelsToRatio(newY, imageDimensions.height),
                widthRatio: pixelsToRatio(newWidth, imageDimensions.width),
                heightRatio: pixelsToRatio(newHeight, imageDimensions.height)
            } : annotation
        ));
    };

    const handleResizeEnd = () => {
        // Get the latest annotations state and notify parent of resize completion
        setAnnotations(prevAnnotations => {
            // Log the final resized annotation position
            if (selectedAnnotationId) {
                const resizedAnnotation = prevAnnotations.find(annotation => annotation.id === selectedAnnotationId);
                if (resizedAnnotation) {
                    console.log('=== BOX RESIZED ===');
                    console.log('Box ID:', selectedAnnotationId);
                    console.log('Final position (ratios):', {
                        xRatio: resizedAnnotation.xRatio,
                        yRatio: resizedAnnotation.yRatio,
                        widthRatio: resizedAnnotation.widthRatio,
                        heightRatio: resizedAnnotation.heightRatio
                    });
                    console.log('=== END BOX RESIZED ===');
                }
            }

            // Notify parent of the resize completion with updated position
            notifyViolationsChange(prevAnnotations);

            return prevAnnotations; // Return unchanged since handleResizeMove already updated it
        });

        setIsResizing(false);
        setResizeHandle(null);
        setResizeStartPoint(null);
        setResizeStartAnnotation(null);
    };

    // Render resize handles for selected annotation
    const renderResizeHandles = (annotation: ViolationData) => {
        if (!annotation.isSelected) return null;

        const width = ratioToPixels(annotation.widthRatio || 0, imageDimensions.width);
        const height = ratioToPixels(annotation.heightRatio || 0, imageDimensions.height);
        const handleSize = 8;

        const handles = [
            { key: 'nw', x: -handleSize / 2, y: -handleSize / 2, cursor: 'nw-resize' },
            { key: 'ne', x: width - handleSize / 2, y: -handleSize / 2, cursor: 'ne-resize' },
            { key: 'sw', x: -handleSize / 2, y: height - handleSize / 2, cursor: 'sw-resize' },
            { key: 'se', x: width - handleSize / 2, y: height - handleSize / 2, cursor: 'se-resize' },
            { key: 'n', x: width / 2 - handleSize / 2, y: -handleSize / 2, cursor: 'n-resize' },
            { key: 's', x: width / 2 - handleSize / 2, y: height - handleSize / 2, cursor: 's-resize' },
            { key: 'w', x: -handleSize / 2, y: height / 2 - handleSize / 2, cursor: 'w-resize' },
            { key: 'e', x: width - handleSize / 2, y: height / 2 - handleSize / 2, cursor: 'e-resize' },
        ];

        return handles.map(handle => (
            <Circle
                key={handle.key}
                x={handle.x}
                y={handle.y}
                radius={handleSize / 2}
                fill="#10b981"
                stroke="#ffffff"
                strokeWidth={2}
                draggable={false}
                onMouseDown={(e) => handleResizeStart(annotation.id, handle.key, e)}
                style={{ cursor: handle.cursor }}
            />
        ));
    };

    // Compute header layout for unpositioned violations with dynamic heights
    const visibleAnnotations = annotations.filter(passesCanvasVisibility);
    const unpositioned = [
        ...visibleAnnotations.filter(isOverallPageScope),
        ...visibleAnnotations.filter(isOffCaptureScope),
    ];
    const overallPageBandCount = unpositioned.filter(isOverallPageScope).length;
    const offCaptureBandCount = unpositioned.filter(isOffCaptureScope).length;
    const positioned = visibleAnnotations.filter(a => hasPosition(a));

    const headerPadding = unpositioned.length > 0 ? 20 : 0;
    const headerTitleHeight = unpositioned.length > 0 ? 28 : 0; // space for header title
    const headerRowGap = 10;
    const headerSeparatorGap = unpositioned.length > 0 ? 20 : 0; // gap between header and PDF

    // Measure each unpositioned violation to compute dynamic box heights and positions
    const measuredUnpositioned = (() => {
        if (unpositioned.length === 0) return [] as Array<{ id: string; boxY: number; boxH: number; titleHeight: number; annotation: ViolationData }>;
        const results: Array<{ id: string; boxY: number; boxH: number; titleHeight: number; annotation: ViolationData }> = [];
        let cursorY = headerPadding / 2 + headerTitleHeight; // start after title
        const boxX = 10;
        const boxW = Math.max(0, imageDimensions.width - 20);
        const textWidth = Math.max(60, boxW - 24);
        const titleFontSize = 14;
        const minRowHeight = 70;
        const categoryBlockHeight = 24; // approx height for category line
        const rowVerticalPadding = 24; // paddings inside row

        unpositioned.forEach((annotation) => {
            if (isOffCaptureScope(annotation)) {
                const { totalHeight } = buildOffCaptureSegments(annotation, textWidth);
                const boxH = Math.max(minRowHeight, Math.ceil(totalHeight));
                results.push({ id: annotation.id, boxY: cursorY, boxH, titleHeight: 0, annotation });
                cursorY += boxH + headerRowGap;
                return;
            }
            const title = getHeaderRowTitle(annotation);
            const wrapped = wrapTextToWidth(String(title), textWidth, titleFontSize, 'sans-serif');
            const titleHeight = Math.max(wrapped.totalHeight, titleFontSize * 1.2);
            const contentHeight = titleHeight + 12 /* spacing */ + categoryBlockHeight + 12 /* bottom padding */;
            const boxH = Math.max(minRowHeight, Math.ceil(contentHeight));
            results.push({ id: annotation.id, boxY: cursorY, boxH, titleHeight, annotation });
            cursorY += boxH + headerRowGap;
        });
        return results;
    })();

    const headerHeight = measuredUnpositioned.length > 0
        ? Math.ceil((measuredUnpositioned[measuredUnpositioned.length - 1].boxY + measuredUnpositioned[measuredUnpositioned.length - 1].boxH) - (headerPadding / 2) + headerPadding / 2 + headerSeparatorGap)
        : 0;

    return (
        <div style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
            backgroundColor: '#f9fafb',
            position: 'relative',
            overflow: 'visible',
        }}>
            {/* Right-Side Drawer / Sliding Panel */}
            <div
                style={{
                    width: sidebarVisible ? `${SIDEBAR_WIDTH_PX}px` : '0px',
                    transition: drawerTransitionsEnabled ? 'width 180ms ease' : 'none',
                    position: 'relative',
                    zIndex: 90,
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'stretch',
                    minHeight: 0,
                }}
            >
                <div
                    style={{
                        width: `${SIDEBAR_WIDTH_PX}px`,
                        backgroundColor: 'white',
                        borderTop: '0px',
                        borderRight: '0px',
                        borderBottom: '0px',
                        borderLeft: `1px solid ${drawerHandleHover ? '#94a3b8' : '#cbd5e1'}`,
                        display: 'flex',
                        flexDirection: 'column',
                        transform: sidebarVisible ? 'translateX(0)' : `translateX(${SIDEBAR_WIDTH_PX}px)`,
                        transition: drawerTransitionsEnabled ? 'transform 180ms ease' : 'none',
                        pointerEvents: sidebarVisible ? 'auto' : 'none',
                        minHeight: 0,
                    }}
                >

                <div style={{
                    padding: '24px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden' // Prevent overflow on this container
                }}>
                    {/* Breadcrumbs moved to AnnotationPage (always visible) */}
                    <h4 style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#374151',
                        margin: '0 0 16px 0',
                        flexShrink: 0 // Prevent title from shrinking
                    }}>
                        📋 違反一覧（{getFilteredBoxes().length}/{allowDeletedInView ? annotations.length : annotations.filter((a) => !a.deleted).length}）
                        {enableUnfixableClassification && fixabilityBreakdown.total > 0 && (
                            <span style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                                — 修正可 {fixabilityBreakdown.fixable} / 修正不可 {fixabilityBreakdown.unfixable}
                            </span>
                        )}
                    </h4>

                    {/* Search and Filter Controls */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        flexShrink: 0 // Prevent controls from shrinking
                    }}>
                        {/* Search Input */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: '500',
                                color: '#64748b',
                                marginBottom: '4px'
                            }}>
                                🔍 違反を検索:
                            </label>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="違反を検索..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: 'white',
                                    minWidth: '0'
                                }}
                            />
                        </div>

                        {/* Category Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: '500',
                                color: '#64748b',
                                marginBottom: '4px'
                            }}>
                                🏷️ カテゴリで絞り込み:
                            </label>
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    minWidth: '0'
                                }}
                            >
                                <option value="">すべてのカテゴリ</option>
                                {VIOLATION_CATEGORIES.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {enableUnfixableClassification && (
                            <div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#64748b',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={showUnfixableViolations}
                                        onChange={(e) => handleShowUnfixableToggle(e.target.checked)}
                                        style={{ accentColor: '#7c3aed' }}
                                    />
                                    修正不可違反を表示
                                </label>
                            </div>
                        )}

                        {/* Active Filters Display */}
                        {(searchTerm || categoryFilter || (enableUnfixableClassification && !showUnfixableViolations)) && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#64748b',
                                    fontWeight: '500'
                                }}>
                                    適用中のフィルター:
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '4px'
                                }}>
                                    {searchTerm && (
                                        <span style={{
                                            fontSize: '10px',
                                            backgroundColor: '#dbeafe',
                                            color: '#1e40af',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            border: '1px solid #93c5fd'
                                        }}>
                                            検索: "{searchTerm}"
                                        </span>
                                    )}
                                    {categoryFilter && (
                                        <span style={{
                                            fontSize: '10px',
                                            backgroundColor: '#dcfce7',
                                            color: '#166534',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            border: '1px solid #86efac'
                                        }}>
                                            カテゴリ: {getCategoryTitle(categoryFilter)}
                                        </span>
                                    )}
                                    {enableUnfixableClassification && !showUnfixableViolations && (
                                        <span style={{
                                            fontSize: '10px',
                                            backgroundColor: '#ede9fe',
                                            color: '#5b21b6',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            border: '1px solid #c4b5fd',
                                        }}>
                                            修正不可を非表示
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: '11px',
                                        backgroundColor: '#f1f5f9',
                                        color: '#64748b',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        alignSelf: 'flex-start'
                                    }}
                                >
                                    フィルターをクリア
                                </button>
                            </div>
                        )}
                    </div>


                    {annotations.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '32px 0',
                            color: '#6b7280'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>違反がありません</div>
                            <div style={{ fontSize: '12px' }}>違反を追加してください</div>
                        </div>
                    ) : getFilteredBoxes().length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '32px 0',
                            color: '#6b7280'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>違反が見つかりません</div>
                            <div style={{ fontSize: '12px' }}>検索条件を調整してください</div>
                        </div>
                    ) : (
                        <div
                            ref={violationListRef}
                            className="violation-list-container"
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                paddingRight: '8px', // Add padding for scrollbar
                                marginRight: '-8px' // Offset the padding to maintain layout
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {getFilteredBoxes()
                                    .sort((a, b) => {
                                        // Handle unpositioned violations - put them at the beginning
                                        const aHasPosition = hasPosition(a);
                                        const bHasPosition = hasPosition(b);

                                        if (!aHasPosition && !bHasPosition) {
                                            // Both unpositioned: keep the same order as the canvas
                                            // header band — overall-page first, then off-capture,
                                            // preserving original order within each group.
                                            const aOff = isOffCaptureScope(a) ? 1 : 0;
                                            const bOff = isOffCaptureScope(b) ? 1 : 0;
                                            if (aOff !== bOff) return aOff - bOff;
                                            return 0;
                                        }
                                        if (!aHasPosition) return -1; // a goes before b
                                        if (!bHasPosition) return 1; // b goes before a

                                        // Both have positions - sort by center point (top to bottom, left to right)
                                        const aCenterY = ratioToPixels((a.yRatio || 0) + (a.heightRatio || 0) / 2, imageDimensions.height);
                                        const bCenterY = ratioToPixels((b.yRatio || 0) + (b.heightRatio || 0) / 2, imageDimensions.height);

                                        // First sort by Y center position (top to bottom)
                                        const yDiff = aCenterY - bCenterY;
                                        if (Math.abs(yDiff) > 5) { // If Y difference is significant (>5px), sort by Y
                                            return yDiff;
                                        }

                                        // If Y positions are similar (same line), sort by X center position (left to right)
                                        const aCenterX = ratioToPixels((a.xRatio || 0) + (a.widthRatio || 0) / 2, imageDimensions.width);
                                        const bCenterX = ratioToPixels((b.xRatio || 0) + (b.widthRatio || 0) / 2, imageDimensions.width);
                                        return aCenterX - bCenterX;
                                    })
                                    .map((annotation, index) => {
                                        const unfixable = isAnnotationUnfixable(annotation);
                                        const listBorderColor = annotation.id === selectedAnnotationId
                                            ? (unfixable ? UNFIXABLE_BOX_STROKE : '#3b82f6')
                                            : (unfixable ? '#c4b5fd' : '#e5e7eb');
                                        const listBg = annotation.id === selectedAnnotationId
                                            ? (unfixable ? '#f5f3ff' : '#eff6ff')
                                            : (unfixable ? '#faf5ff' : '#f9fafb');

                                        return (
                                            <div
                                                key={annotation.id}
                                                data-violation-id={annotation.id}
                                                style={{
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid',
                                                    borderColor: listBorderColor,
                                                    backgroundColor: listBg,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    marginBottom: '8px'
                                                }}
                                                onClick={() => handleAnnotationClick(annotation.id)}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    marginBottom: '4px'
                                                }}>
                                                    <span style={{
                                                        fontSize: '12px',
                                                        fontWeight: '500',
                                                        color: '#6b7280',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        Box {index + 1}
                                                        {unfixable && (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                color: '#5b21b6',
                                                                backgroundColor: '#ede9fe',
                                                                padding: '1px 6px',
                                                                borderRadius: '8px',
                                                                border: '1px solid #c4b5fd',
                                                            }}>
                                                                修正不可
                                                            </span>
                                                        )}
                                                        {annotation.id === selectedAnnotationId && (
                                                            <span style={{ color: unfixable ? UNFIXABLE_BOX_STROKE : '#3b82f6' }}>📍</span>
                                                        )}
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedAnnotationId(annotation.id);
                                                            setTimeout(() => deleteSelectedAnnotation(), 100);
                                                        }}
                                                        style={{
                                                            color: '#ef4444',
                                                            fontSize: '12px',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                                {annotation.id === selectedAnnotationId ? (
                                                    <textarea
                                                        ref={resizeTextareaToContent}
                                                        value={annotation.editNote || ''}
                                                        onChange={(e) => {
                                                            resizeTextareaToContent(e.currentTarget);
                                                            handleTextEdit(annotation.id, e.target.value);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        placeholder="修正指摘内容を入力..."
                                                        style={{
                                                            width: '100%',
                                                            fontSize: `${VIOLATION_NOTE_FONT_SIZE_PX}px`,
                                                            color: '#111827',
                                                            marginBottom: '8px',
                                                            lineHeight: VIOLATION_NOTE_LINE_HEIGHT,
                                                            padding: '6px 8px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '6px',
                                                            resize: 'none',
                                                            overflow: 'hidden',
                                                            boxSizing: 'border-box',
                                                            fontFamily: 'inherit',
                                                            minHeight: '20px'
                                                        }}
                                                        rows={1}
                                                    />
                                                ) : (
                                                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                                                        <div
                                                            ref={(el) => {
                                                                const fadeEl = el?.nextElementSibling as HTMLElement | null;
                                                                if (!fadeEl) return;
                                                                const isTruncated = !!el && el.scrollHeight > el.clientHeight + 1;
                                                                fadeEl.style.display = isTruncated ? 'flex' : 'none';
                                                            }}
                                                            style={{
                                                                fontSize: `${VIOLATION_NOTE_FONT_SIZE_PX}px`,
                                                                color: '#111827',
                                                                lineHeight: VIOLATION_NOTE_LINE_HEIGHT,
                                                                whiteSpace: 'pre-wrap',
                                                                wordWrap: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                minHeight: '20px',
                                                                maxHeight: `${VIOLATION_NOTE_MAX_HEIGHT_PX}px`,
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            {annotation.editNote ? highlightSearchTerm(annotation.editNote, searchTerm) : 'No annotation'}
                                                        </div>
                                                        {/* Fade shown only when the note is clipped by VIOLATION_NOTE_MAX_HEIGHT_PX; toggled via the ref above */}
                                                        <div style={{
                                                            display: 'none',
                                                            position: 'absolute',
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            height: '22px',
                                                            background: `linear-gradient(to bottom, ${listBg}00, ${listBg} 65%)`,
                                                            pointerEvents: 'none'
                                                        }} />
                                                    </div>
                                                )}

                                                {/* Category Display (shown only when not being edited via the dropdown below) */}
                                                {annotation.id !== selectedAnnotationId && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginBottom: '4px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: '#6b7280',
                                                            fontWeight: '500'
                                                        }}>
                                                            カテゴリ:
                                                        </span>
                                                        {annotation.category ? (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: '#059669',
                                                                backgroundColor: '#ecfdf5',
                                                                padding: '2px 8px',
                                                                borderRadius: '12px',
                                                                border: '1px solid #a7f3d0',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                maxWidth: '150px',
                                                                display: 'inline-block'
                                                            }}>
                                                                {highlightSearchTerm(getCategoryTitle(annotation.category), searchTerm)}
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: '#6b7280',
                                                                fontStyle: 'italic'
                                                            }}>
                                                                カテゴリ未設定
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Category Selection Dropdown */}
                                                {annotation.id === selectedAnnotationId && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <div style={{
                                                            position: 'relative',
                                                            display: 'inline-block',
                                                            width: '100%'
                                                        }} ref={categoryDropdownRef}>
                                                            <button
                                                                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 12px',
                                                                    fontSize: '12px',
                                                                    border: '1px solid #d1d5db',
                                                                    borderRadius: '6px',
                                                                    backgroundColor: '#ffffff',
                                                                    color: '#374151',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between'
                                                                }}
                                                            >
                                                                <span>{annotation.category ? getCategoryTitle(annotation.category) : 'カテゴリを選択'}</span>
                                                                <span>▼</span>
                                                            </button>
                                                            {showCategoryDropdown && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    backgroundColor: '#ffffff',
                                                                    border: '1px solid #d1d5db',
                                                                    borderRadius: '6px',
                                                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                                    zIndex: 1000,
                                                                    marginTop: '2px'
                                                                }}>
                                                                    {VIOLATION_CATEGORIES.map((category) => (
                                                                        <button
                                                                            key={category.id}
                                                                            onClick={() => {
                                                                                handleCategoryEdit(annotation.id, category.id);
                                                                                setShowCategoryDropdown(false);
                                                                            }}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '8px 12px',
                                                                                fontSize: '12px',
                                                                                border: 'none',
                                                                                backgroundColor: 'transparent',
                                                                                color: '#374151',
                                                                                cursor: 'pointer',
                                                                                textAlign: 'left',
                                                                                borderBottom: '1px solid #f3f4f6'
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#f9fafb';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                                            }}
                                                                        >
                                                                            {category.title}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* Edge handle button (always visible) */}
            <button
                type="button"
                onClick={toggleSidebar}
                onMouseEnter={() => setDrawerHandleHover(true)}
                onMouseLeave={() => setDrawerHandleHover(false)}
                onFocus={() => setDrawerHandleHover(true)}
                onBlur={() => setDrawerHandleHover(false)}
                aria-label={sidebarVisible ? '違反一覧サイドバーを閉じる' : '違反一覧サイドバーを開く'}
                style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: `${sidebarVisible ? SIDEBAR_WIDTH_PX : 0}px`,
                    width: '22px',
                    height: '108px',
                    borderRadius: '12px 0 0 12px',
                    borderTop: `1px solid ${drawerHandleHover ? '#94a3b8' : '#cbd5e1'}`,
                    borderLeft: `1px solid ${drawerHandleHover ? '#94a3b8' : '#cbd5e1'}`,
                    borderBottom: `1px solid ${drawerHandleHover ? '#94a3b8' : '#cbd5e1'}`,
                    borderRight: 'none',
                    backgroundColor: 'white',
                    color: '#0f172a',
                    cursor: 'pointer',
                    zIndex: 200,
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'background-color 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
                }}
                title={sidebarVisible ? '違反一覧を閉じる' : '違反一覧を開く'}
            >
                <span
                    aria-hidden
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <span style={{ fontSize: '12px', fontWeight: 900, lineHeight: 1, color: '#0f172a' }}>
                        {sidebarVisible ? '>' : '<'}
                    </span>
                    <span aria-hidden style={{ width: '12px', height: '1px', backgroundColor: '#e2e8f0' }} />
                    <span
                        style={{
                            writingMode: 'vertical-rl',
                            textOrientation: 'upright',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            lineHeight: 1,
                            color: '#475569',
                            userSelect: 'none',
                        }}
                    >
                        違反リスト
                    </span>
                </span>
            </button>

            {/* Main Canvas Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0 // Allow flex item to shrink below content size
            }}>
                {/* Fixed Header - Not scrollable */}
                {/* Top header removed to maximize canvas area (controls are now sticky inside the canvas). */}

                {/* Category Content Existence Dictionary - Main Canvas Editing Area */}
                {onCategoryContentExistenceDictChange && (
                    <div style={{
                        padding: '12px 20px',
                        backgroundColor: '#f0f9ff',
                        borderBottom: '2px solid #3b82f6',
                        flexShrink: 0,
                        zIndex: 99
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                        }}
                            onClick={() => setIsCategoryContentExistenceExpanded(!isCategoryContentExistenceExpanded)}
                        >
                            <h4 style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#1e40af',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                userSelect: 'none'
                            }}>
                                カテゴリコンテンツ存在管理
                            </h4>
                            <span style={{
                                fontSize: '16px',
                                color: '#1e40af',
                                transition: 'transform 0.2s',
                                transform: isCategoryContentExistenceExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                userSelect: 'none'
                            }}>
                                ▼
                            </span>
                        </div>
                        {isCategoryContentExistenceExpanded && (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                gap: '8px',
                                maxHeight: '180px',
                                overflowY: 'auto',
                                padding: '8px 0 4px 0',
                                marginTop: '8px',
                                borderTop: '1px solid #bfdbfe'
                            }}>
                                {VIOLATION_CATEGORIES.length === 0 ? (
                                    <div style={{
                                        padding: '12px',
                                        textAlign: 'center',
                                        color: '#6b7280',
                                        fontSize: '13px'
                                    }}>
                                        カテゴリがありません
                                    </div>
                                ) : (
                                    VIOLATION_CATEGORIES.map((category) => {
                                        const isChecked = categoryContentExistenceDict[category.id] ?? false;
                                        return (
                                            <label
                                                key={category.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontSize: '13px',
                                                    color: '#374151',
                                                    cursor: 'pointer',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    backgroundColor: isChecked ? '#dbeafe' : '#ffffff',
                                                    border: `1px solid ${isChecked ? '#3b82f6' : '#cbd5e1'}`,
                                                    transition: 'all 0.2s',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = '#3b82f6';
                                                    e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#f0f9ff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = isChecked ? '#3b82f6' : '#cbd5e1';
                                                    e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#ffffff';
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        if (onCategoryContentExistenceDictChange) {
                                                            onCategoryContentExistenceDictChange({
                                                                ...categoryContentExistenceDict,
                                                                [category.id]: e.target.checked
                                                            });
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        cursor: 'pointer',
                                                        width: '16px',
                                                        height: '16px',
                                                        accentColor: '#3b82f6',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <span style={{
                                                    fontWeight: isChecked ? '500' : '400',
                                                    color: isChecked ? '#1e40af' : '#374151',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {category.title}
                                                </span>
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Canvas Container - Scrollable */}
                <div 
                    id="canvas-container" 
                    ref={scrollContainerRef}
                    style={{
                        flex: 1,
                        position: 'relative',
                        backgroundColor: '#f3f4f6',
                        overflow: 'auto',
                        minHeight: 0
                    }}
                >
                    {/* Floating controls near the PDF canvas */}
                    <div
                        style={{
                            position: 'sticky',
                            top: '24px',
                            zIndex: 50,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: '8px',
                            paddingRight: '24px',
                            paddingLeft: '24px'
                        }}
                    >
                        {errorText && (
                            <div
                                style={{
                                    maxWidth: '100%',
                                    backgroundColor: '#fee2e2',
                                    color: '#991b1b',
                                    border: '1px solid #fecaca',
                                    borderRadius: '8px',
                                    padding: '8px 10px',
                                    fontSize: '12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                {errorText}
                            </div>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                width: '100%',
                                flexWrap: 'wrap'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '12px',
                                    color: '#374151',
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    padding: '6px 8px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={zoomPanelEnabled}
                                    onChange={(e) => setZoomPanelEnabled(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>ズームパネル</span>
                            </label>
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '12px',
                                    color: '#374151',
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    padding: '6px 8px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={labelsAboveBoxesEnabled}
                                    onChange={(e) => setLabelsAboveBoxesEnabled(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>修正指摘内容を表示</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <button
                                    style={{
                                        padding: '8px 10px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#374151',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: 'help',
                                        whiteSpace: 'pre-line',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#e5e7eb'; setShowShortcutsTooltip(true); }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; setShowShortcutsTooltip(false); }}
                                    onFocus={() => setShowShortcutsTooltip(true)}
                                    onBlur={() => setShowShortcutsTooltip(false)}
                                >
                                    ⌨︎ ショートカット
                                </button>
                                {showShortcutsTooltip && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 'calc(100% + 6px)',
                                            right: 0,
                                            width: '360px',
                                            backgroundColor: '#111827',
                                            color: '#f9fafb',
                                            border: '1px solid #374151',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            fontSize: '12px',
                                            lineHeight: '1.5',
                                            whiteSpace: 'pre-line',
                                            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {SHORTCUTS_TOOLTIP}
                                    </div>
                                )}
                            </div>
                            {enableUnfixableClassification && hasPageUnfixableRule && (
                                <div style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        style={{
                                            padding: '8px 10px',
                                            backgroundColor: showUnfixableRuleTooltip ? '#ede9fe' : '#f3f4f6',
                                            color: '#5b21b6',
                                            border: `1px solid ${showUnfixableRuleTooltip ? '#c4b5fd' : '#e5e7eb'}`,
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            cursor: 'help',
                                            whiteSpace: 'nowrap',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        }}
                                        onMouseOver={() => setShowUnfixableRuleTooltip(true)}
                                        onMouseOut={() => setShowUnfixableRuleTooltip(false)}
                                        onFocus={() => setShowUnfixableRuleTooltip(true)}
                                        onBlur={() => setShowUnfixableRuleTooltip(false)}
                                        aria-label="メディア会社の修正不可内容を表示"
                                    >
                                        📋 修正不可ルール
                                    </button>
                                    {showUnfixableRuleTooltip && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 'calc(100% + 6px)',
                                                left: 0,
                                                width: 'min(520px, calc(100vw - 48px))',
                                                maxHeight: 'min(60vh, 440px)',
                                                overflowY: 'auto',
                                                backgroundColor: '#ffffff',
                                                color: '#374151',
                                                border: '1px solid #c4b5fd',
                                                borderRadius: '8px',
                                                padding: '12px 14px',
                                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                                zIndex: 60,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    color: '#5b21b6',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                メディア会社の修正不可内容（ガイドライン全文）
                                            </div>
                                            <FixabilityEvidenceBlock
                                                mediaRuleDescription={pageUnfixableRuleDescription}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>

                            {(onSave || onMarkReviewed) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                                    <button
                                        onClick={addUnpositionedViolation}
                                        style={{
                                            padding: '6px 10px',
                                            minWidth: '104px',
                                            textAlign: 'center',
                                            backgroundColor: '#10b981',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#0ea472'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; }}
                                    >
                                        ＋ 全体違反
                                    </button>
                                    <button
                                        onClick={addOffCaptureViolation}
                                        title="フルページキャプチャ失敗によりキャプチャに写っていない違反を追加します"
                                        style={{
                                            padding: '6px 10px',
                                            minWidth: '104px',
                                            textAlign: 'center',
                                            backgroundColor: '#f59e0b',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; }}
                                    >
                                        ＋ キャプチャ外違反
                                    </button>
                                    {onSave && (
                                        <button
                                            onClick={onSave}
                                            disabled={saveLoading}
                                            style={{
                                                padding: '6px 10px',
                                                minWidth: '104px',
                                                textAlign: 'center',
                                                backgroundColor: '#ffffff',
                                                color: '#374151',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                                opacity: saveLoading ? 0.7 : 1,
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                            }}
                                            onMouseOver={(e) => { if (!saveLoading) e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                                        >
                                            {saveLoading ? '保存中...' : '保存'}
                                        </button>
                                    )}
                                    {onMarkReviewed && (
                                        <button
                                            onClick={onMarkReviewed}
                                            disabled={reviewLoading}
                                            style={{
                                                padding: '6px 10px',
                                                minWidth: '104px',
                                                textAlign: 'center',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: 700,
                                                cursor: reviewLoading ? 'not-allowed' : 'pointer',
                                                opacity: reviewLoading ? 0.7 : 1,
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                            }}
                                            onMouseOver={(e) => { if (!reviewLoading) e.currentTarget.style.filter = 'brightness(1.03)'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.filter = 'none'; }}
                                        >
                                            {reviewLoading ? '処理中...' : 'レビュー済み'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Zoomed View for Selected Violation */}
                    {zoomPanelEnabled && showZoomedView && zoomedImageUrl && selectedAnnotationId && zoomedViewPosition && !isEditingAnnotation && (() => {
                        const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);
                        if (!selectedAnnotation || !hasPosition(selectedAnnotation)) return null;

                        const zoomPanelUnfixable = isAnnotationUnfixable(selectedAnnotation);

                        return (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: `${zoomedViewPosition.top}px`,
                                    left: `${zoomedViewPosition.left}px`,
                                    width: `${zoomedViewPosition.width}px`,
                                    zIndex: 55,
                                    backgroundColor: '#ffffff',
                                    border: `2px solid ${zoomPanelUnfixable ? UNFIXABLE_BOX_STROKE : '#3b82f6'}`,
                                    borderRadius: '8px',
                                    padding: '12px',
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                    maxHeight: 'min(85vh, 720px)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <h4 style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        margin: 0
                                    }}>
                                        🔍 拡大表示
                                    </h4>
                                    <button
                                        onClick={() => setShowZoomedView(false)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '18px',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            padding: '0',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.color = '#374151'}
                                        onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div style={{
                                    flex: '0 1 auto',
                                    overflow: 'auto',
                                    maxHeight: '280px',
                                    borderRadius: '4px',
                                    border: '1px solid #e5e7eb',
                                    marginBottom: '8px'
                                }}>
                                    <img
                                        src={zoomedImageUrl}
                                        alt="ズームした違反箇所"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </div>
                                {selectedAnnotation.editNote && (
                                    <div style={{
                                        flexShrink: 0,
                                        padding: '8px',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#374151',
                                        maxHeight: '80px',
                                        overflow: 'auto',
                                        marginBottom: '8px',
                                    }}>
                                        <strong>修正指摘内容:</strong> {selectedAnnotation.editNote}
                                    </div>
                                )}
                                {selectedAnnotation.category && (
                                    <div style={{
                                        flexShrink: 0,
                                        marginBottom: '8px',
                                        fontSize: '11px',
                                        color: '#6b7280'
                                    }}>
                                        カテゴリ: {getCategoryTitle(selectedAnnotation.category)}
                                    </div>
                                )}
                                {enableUnfixableClassification && (
                                    <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                                        <ViolationFixabilityControls
                                            violation={selectedAnnotation}
                                            mediaRuleDescription={pageUnfixableRuleDescription}
                                            readOnly
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    {(() => {
                        // Using single image - container height matches image dimensions
                        const actualContainerHeight = imageDimensions.height;
                        // While fetching, imageDimensions are still 0×0 — use a viewport-based minimum
                        // so loading/error messages are not squeezed (same sidebar offset as scaled image logic).
                        const canvasAreaFallbackW = Math.max(
                            400,
                            (typeof window !== 'undefined' ? window.innerWidth : 1200) - 320 - 80
                        );
                        const statusPanelMinH = 240;
                        const statusOverlayWidth = Math.max(imageDimensions.width || 0, canvasAreaFallbackW);
                        const statusOverlayHeight = Math.max(imageDimensions.height || 0, statusPanelMinH);
                        const needsStatusPanel = !!(imageError || imageLoading);
                        const layoutMinHeight = needsStatusPanel
                            ? Math.max(actualContainerHeight + headerHeight, statusOverlayHeight + headerHeight + 60)
                            : actualContainerHeight + headerHeight;

                        return (
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                minHeight: `${layoutMinHeight}px`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                padding: '20px',
                                height: 'auto'
                            }}>
                        {/* Image Background */}
                        {imageError ? (
                            <div style={{
                                position: 'absolute',
                                top: `${20 + headerHeight}px`,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: `${statusOverlayWidth}px`,
                                maxWidth: 'calc(100vw - 40px)',
                                minHeight: `${statusOverlayHeight}px`,
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                                boxSizing: 'border-box',
                                padding: '8px 4px',
                            }}>
                                <div style={{
                                    textAlign: 'center',
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '500',
                                        color: '#dc2626',
                                        margin: '0 0 8px 0'
                                    }}>画像の読み込みに失敗しました</h3>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        margin: '0 0 16px 0',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        lineHeight: 1.5,
                                    }}>エラー: {imageError}</p>
                                    <button
                                        onClick={() => {
                                            attemptedLoadRef.current = null; // Clear attempted load ref
                                            setImageLoading(true);
                                            setImageError(null);
                                            loadImageDocument();
                                        }}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        画像を再読み込み
                                    </button>
                                </div>
                            </div>
                        ) : imageLoading ? (
                            <div style={{
                                position: 'absolute',
                                top: '20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: `${statusOverlayWidth}px`,
                                maxWidth: 'calc(100vw - 40px)',
                                minHeight: `${statusOverlayHeight}px`,
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                                boxSizing: 'border-box',
                                padding: '8px 4px',
                            }}>
                                <div style={{
                                    textAlign: 'center',
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '500',
                                        color: '#6b7280',
                                        margin: '0 0 8px 0'
                                    }}>画像を読み込み中...</h3>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#9ca3af',
                                        margin: '0',
                                        lineHeight: 1.5,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}>読み込みが完了するまでお待ちください</p>
                                </div>
                            </div>
                        ) : (
                            <div
                                ref={imageContainerRef}
                                style={{
                                    position: 'absolute',
                                    top: `${20 + headerHeight}px`,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: `${imageDimensions.width}px`,
                                    height: `${actualContainerHeight}px`,
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                {/* Render the background as a single image (performance: Konva Stage is the bottleneck, not image rendering) */}
                                {imageElement ? (
                                    <img
                                        onLoad={() => {
                                            console.log('[ViolationCanvas] Single <img> rendered (using single image for performance - Konva Stage is the bottleneck)');
                                        }}
                                        src={imageElement.src}
                                        alt="ドキュメント"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '8px'
                                        }}
                                    />
                                ) : null}
                            </div>
                        )}

                        {/* Konva Canvas Overlay - Viewport-based rendering for performance */}
                        {(() => {
                            const PADDING = 500; // Padding around viewport for smooth scrolling
                            const stageWidth = window.innerWidth + PADDING * 2;
                            const stageHeight = window.innerHeight + PADDING * 2;
                            
                            console.log('[ViolationCanvas] ✅ Viewport-based Konva Stage:', {
                                stageSize: { width: stageWidth, height: stageHeight },
                                imageSize: { width: imageDimensions.width, height: actualContainerHeight + headerHeight },
                                stagePosition,
                                padding: PADDING
                            });
                            
                            return (
                                <div 
                                    style={{
                                        width: `${imageDimensions.width}px`,
                                        height: `${actualContainerHeight + headerHeight}px`,
                                        overflow: 'hidden',
                                        position: 'absolute',
                                        top: '20px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <div 
                                        ref={stageContainerRef}
                                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                                    >
                                        <Stage
                                            key={`stage-viewport-${stageWidth}-${stageHeight}`}
                                            ref={stageRef}
                                            width={stageWidth}
                                            height={stageHeight}
                                            x={stagePosition.x}
                                            y={stagePosition.y}
                                            scaleX={1}
                                            scaleY={1}
                                            pixelRatio={devicePixelRatio}
                                            onMouseDown={handleMouseDown}
                                            onMouseMove={handleMouseMove}
                                            onMouseUp={handleMouseUp}
                                            style={{ pointerEvents: 'auto', userSelect: 'none', WebkitUserSelect: 'none' }}
                                        >
                                <Layer>
                                    {/* Header area for unpositioned violations */}
                                    {headerHeight > 0 && (
                                        <Group x={0} y={0}>
                                            <Rect
                                                x={0}
                                                y={0}
                                                width={imageDimensions.width}
                                                height={headerHeight}
                                                fill={'#ffffff'}
                                                stroke={'#e5e7eb'}
                                                strokeWidth={1}
                                            />
                                            <Text
                                                text={
                                                    offCaptureBandCount > 0
                                                        ? `サイト全体的の違反 (${overallPageBandCount})　／　キャプチャ外違反 (${offCaptureBandCount})`
                                                        : `サイト全体的の違反 (${overallPageBandCount})`
                                                }
                                                x={12}
                                                y={8}
                                                fontSize={14}
                                                fill={'#6b7280'}
                                                fontStyle="bold"
                                            />
                                            {measuredUnpositioned.map((row) => {
                                                const { annotation, boxY, boxH, titleHeight } = row;
                                            const title = getHeaderRowTitle(annotation);
                                            const cat = annotation.category ? getCategoryTitle(annotation.category) : 'カテゴリ未設定';
                                            const offCapture = isOffCaptureScope(annotation);
                                                const boxX = 10;
                                                const boxW = Math.max(0, imageDimensions.width - 20);
                                                const segTextWidth = Math.max(60, boxW - 24);
                                                return (
                                                    <Group
                                                        key={`unpos-${annotation.id}`}
                                                        x={0}
                                                        y={0}
                                                        onClick={() => handleAnnotationClick(annotation.id)}
                                                        onDblClick={() => handleAnnotationDoubleClick(annotation.id)}
                                                    >
                                                        <Rect
                                                            x={boxX}
                                                            y={boxY}
                                                            width={boxW}
                                                            height={boxH}
                                                            fill={
                                                                isAnnotationUnfixable(annotation)
                                                                    ? (annotation.id === selectedAnnotationId ? '#f5f3ff' : '#faf5ff')
                                                                    : (annotation.id === selectedAnnotationId ? '#eff6ff' : '#f9fafb')
                                                            }
                                                            stroke={
                                                                isAnnotationUnfixable(annotation)
                                                                    ? UNFIXABLE_BOX_STROKE
                                                                    : (annotation.id === selectedAnnotationId ? '#3b82f6' : '#e5e7eb')
                                                            }
                                                            cornerRadius={6}
                                                        />
                                                        {offCapture ? (
                                                            buildOffCaptureSegments(annotation, segTextWidth).segments.map((seg, segIdx) => {
                                                                const sx = boxX + 12;
                                                                const sy = boxY + seg.y;
                                                                if (seg.kind === 'label') {
                                                                    return (
                                                                        <Text
                                                                            key={`seg-${segIdx}`}
                                                                            text={seg.text}
                                                                            x={sx}
                                                                            y={sy}
                                                                            width={segTextWidth}
                                                                            fontSize={seg.fontSize}
                                                                            fontStyle="bold"
                                                                            fill={'#6b7280'}
                                                                        />
                                                                    );
                                                                }
                                                                if (seg.kind === 'category') {
                                                                    return (
                                                                        <Text
                                                                            key={`seg-${segIdx}`}
                                                                            text={seg.text}
                                                                            x={sx}
                                                                            y={sy}
                                                                            width={segTextWidth}
                                                                            fontSize={seg.fontSize}
                                                                            fill={'#6b7280'}
                                                                        />
                                                                    );
                                                                }
                                                                // richtext: render each wrapped run, highlighting URLs inline
                                                                return (
                                                                    <Group key={`seg-${segIdx}`}>
                                                                        {seg.layout.lines.map((line, lineIdx) =>
                                                                            line.map((run, runIdx) => (
                                                                                <Text
                                                                                    key={`run-${lineIdx}-${runIdx}`}
                                                                                    text={run.text}
                                                                                    x={sx + run.x}
                                                                                    y={sy + lineIdx * seg.layout.lineHeight}
                                                                                    fontSize={seg.fontSize}
                                                                                    fill={run.isUrl ? '#2563eb' : (seg.muted ? '#9ca3af' : '#111827')}
                                                                                    textDecoration={run.isUrl ? 'underline' : undefined}
                                                                                    onClick={run.isUrl && run.url ? (e) => { e.cancelBubble = true; openUrlInNewTab(run.url!); } : undefined}
                                                                                    onTap={run.isUrl && run.url ? (e) => { e.cancelBubble = true; openUrlInNewTab(run.url!); } : undefined}
                                                                                    onMouseEnter={run.isUrl ? (e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'pointer'; } : undefined}
                                                                                    onMouseLeave={run.isUrl ? (e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; } : undefined}
                                                                                />
                                                                            ))
                                                                        )}
                                                                    </Group>
                                                                );
                                                            })
                                                        ) : (
                                                            <>
                                                                <Text
                                                                    text={title}
                                                                    x={boxX + 12}
                                                                    y={boxY + 10}
                                                                    width={boxW - 24}
                                                                    fontSize={14}
                                                                    fill={'#111827'}
                                                                />
                                                                <Text
                                                                    text={`カテゴリ: ${cat}`}
                                                                    x={boxX + 12}
                                                                    y={boxY + 12 + Math.ceil(titleHeight)}
                                                                    width={boxW - 24}
                                                                    fontSize={12}
                                                                    fill={'#6b7280'}
                                                                />
                                                            </>
                                                        )}
                                                    </Group>
                                                );
                                            })}
                                        </Group>
                                    )}
                                    {/* Existing annotations (exclude deleted ones). Ensure selected renders last (on top). */}
                                    {(() => {
                                        const visible = positioned; // Only positioned in canvas overlay
                                        const nonSelected = visible.filter(a => !a.isSelected);
                                        const selected = visible.filter(a => a.isSelected);
                                        const ordered = [...nonSelected, ...selected];

                                        // Precompute selected bounds for overlap detection
                                        const selectedAnn = selected[0];
                                        let selectedBox: { x: number; y: number; width: number; height: number } | null = null;
                                        let selectedLabel: { x: number; y: number; width: number; height: number } | null = null;
                                        if (selectedAnn) {
                                            const selX = ratioToPixels(selectedAnn.xRatio || 0, imageDimensions.width);
                                            const selY = headerHeight + ratioToPixels(selectedAnn.yRatio || 0, imageDimensions.height);
                                            const selW = ratioToPixels(selectedAnn.widthRatio || 0, imageDimensions.width);
                                            const selH = ratioToPixels(selectedAnn.heightRatio || 0, imageDimensions.height);
                                            selectedBox = { x: selX, y: selY, width: selW, height: selH };
                                            const layout = computeLabelLayout(selectedAnn, imageDimensions.width, imageDimensions.height, 14);
                                            selectedLabel = { x: selX, y: selY + layout.textY, width: layout.bgWidth, height: layout.bgHeight };
                                        }

                                        return ordered.map((annotation) => {
                                            // Calculate overlap for this annotation
                                            let textOverlaps = false;
                                            if (selectedAnn && annotation.id !== selectedAnn.id && selectedBox && selectedLabel) {
                                                const groupX = ratioToPixels(annotation.xRatio || 0, imageDimensions.width);
                                                const groupY = headerHeight + ratioToPixels(annotation.yRatio || 0, imageDimensions.height);
                                                const rectW = ratioToPixels(annotation.widthRatio || 0, imageDimensions.width);
                                                const rectH = ratioToPixels(annotation.heightRatio || 0, imageDimensions.height);
                                                const otherBox = { x: groupX, y: groupY, width: rectW, height: rectH };
                                                const layout = computeLabelLayout(annotation, imageDimensions.width, imageDimensions.height, 14);
                                                const otherLabel = { x: groupX, y: groupY + layout.textY, width: layout.bgWidth, height: layout.bgHeight };
                                                textOverlaps =
                                                    rectsOverlap(selectedBox, otherBox) ||
                                                    rectsOverlap(selectedBox, otherLabel) ||
                                                    rectsOverlap(selectedLabel, otherBox) ||
                                                    rectsOverlap(selectedLabel, otherLabel);
                                            }

                                            return (
                                                <Group
                                                    key={annotation.id}
                                                    x={ratioToPixels(annotation.xRatio || 0, imageDimensions.width)}
                                                    y={headerHeight + ratioToPixels(annotation.yRatio || 0, imageDimensions.height)}
                                                    draggable
                                                    opacity={1}
                                                    onDragEnd={(e) => handleDragEnd(annotation.id, e)}
                                                    onClick={() => handleAnnotationClick(annotation.id)}
                                                    onDblClick={() => handleAnnotationDoubleClick(annotation.id)}
                                                >
                                                    {/* Preview text above the annotation with at most one newline insertion */}
                                                    {/* Hide text label if it overlaps with selected violation; hide when labels disabled */}
                                                    {labelsAboveBoxesEnabled && annotation.editNote && !textOverlaps && (() => {
                                                        const fontSize = 14;
                                                        const layout = computeLabelLayout(annotation, imageDimensions.width, imageDimensions.height, fontSize);
                                                        const { bgWidth, bgHeight, textY, padding } = layout;
                                                        return (
                                                            <Group>
                                                                <Rect
                                                                    x={0}
                                                                    y={textY}
                                                                    width={bgWidth}
                                                                    height={bgHeight}
                                                                    fill="rgba(255, 255, 255, 0.95)"
                                                                    cornerRadius={6}
                                                                />
                                                                <Text
                                                                    text={layout.lines.join('\n')}
                                                                    x={padding}
                                                                    y={textY + padding}
                                                                    width={bgWidth - padding * 2}
                                                                    wrap={'none'}
                                                                    fontSize={fontSize}
                                                                    fill="#1e293b"
                                                                    align="left"
                                                                    lineHeight={1.2}
                                                                    fontStyle="normal"
                                                                    fontVariant="normal"
                                                                />
                                                            </Group>
                                                        );
                                                    })()}

                                                    <Rect
                                                        width={ratioToPixels(annotation.widthRatio || 0, imageDimensions.width)}
                                                        height={ratioToPixels(annotation.heightRatio || 0, imageDimensions.height)}
                                                        fill={
                                                            annotation.isSelected
                                                                ? (isAnnotationUnfixable(annotation)
                                                                    ? UNFIXABLE_BOX_FILL
                                                                    : DEFAULT_BOX_FILL)
                                                                : 'transparent'
                                                        }
                                                        stroke={
                                                            isAnnotationUnfixable(annotation)
                                                                ? UNFIXABLE_BOX_STROKE
                                                                : DEFAULT_BOX_STROKE
                                                        }
                                                        strokeWidth={
                                                            annotation.isSelected
                                                                ? SELECTED_BOX_STROKE_WIDTH
                                                                : DEFAULT_BOX_STROKE_WIDTH
                                                        }
                                                        cornerRadius={6}
                                                    />

                                                    {/* Show placeholder text only if no annotation */}
                                                    {!annotation.editNote && (
                                                        <Text
                                                            text="Double-click to add annotation"
                                                            width={ratioToPixels(annotation.widthRatio || 0, imageDimensions.width)}
                                                            height={ratioToPixels(annotation.heightRatio || 0, imageDimensions.height)}
                                                            fontSize={Math.max(10, Math.min(14, ratioToPixels(annotation.widthRatio || 0, imageDimensions.width) / 9))}
                                                            fill="#94a3b8"
                                                            align="center"
                                                            verticalAlign="middle"
                                                            padding={8}
                                                            backgroundColor="rgba(255, 255, 255, 0.8)"
                                                            cornerRadius={4}
                                                            fontStyle="italic"
                                                        />
                                                    )}

                                                    {/* Resize handles */}
                                                    {renderResizeHandles(annotation)}
                                                </Group>
                                            );
                                        });
                                    })()}

                                    {/* Current drawing annotation */}
                                    {currentAnnotation && (
                                        <Rect
                                            x={currentAnnotation.x}
                                            y={currentAnnotation.y}
                                            width={currentAnnotation.width}
                                            height={currentAnnotation.height}
                                            fill="rgba(102, 126, 234, 0.1)"
                                            stroke="#667eea"
                                            strokeWidth={2}
                                            dash={[6, 6]}
                                            cornerRadius={6}
                                        />
                                    )}
                                </Layer>
                                        </Stage>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                        );
                    })()}
                </div>


            </div>

            {/* Text editing modal */}
            {editingAnnotationId && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '24px',
                            width: '640px',
                            maxWidth: 'calc(100vw - 32px)',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            margin: '0 16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            margin: '0 0 16px 0'
                        }}>
                            {annotations.find(a => a.id === editingAnnotationId)?.scope === 'off_capture'
                                ? 'キャプチャ外違反を編集'
                                : '指摘を編集'}
                        </h3>

                        {/* Cropped preview of the violation area, so reviewers can confirm the content without leaving the popup */}
                        {editingAnnotationImageUrl && (
                            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                                <img
                                    src={editingAnnotationImageUrl}
                                    alt="違反箇所のプレビュー"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '360px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                    }}
                                />
                            </div>
                        )}

                        {/* Capture URL (off-capture violations only) — shown above 違反内容 */}
                        {annotations.find(a => a.id === editingAnnotationId)?.scope === 'off_capture' && (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    キャプチャURL:
                                </label>
                                <input
                                    type="url"
                                    value={editCaptureUrl}
                                    onChange={(e) => {
                                        setEditCaptureUrl(e.target.value);
                                        setCaptureUrlPreviewError(false);
                                    }}
                                    placeholder="別システムで取得したキャプチャのURLを入力..."
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <p style={{
                                    margin: '6px 0 0 0',
                                    fontSize: '12px',
                                    color: '#6b7280',
                                }}>
                                    キャプチャ外違反は別システムでスクリーンショットを取得します。そのURLを貼り付けてください。
                                </p>
                                {editCaptureUrl && !captureUrlPreviewError && (
                                    <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                        <img
                                            key={editCaptureUrl}
                                            src={editCaptureUrl}
                                            alt="キャプチャ画像のプレビュー"
                                            onError={() => setCaptureUrlPreviewError(true)}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '360px',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '6px',
                                            }}
                                        />
                                    </div>
                                )}
                                {editCaptureUrl && captureUrlPreviewError && (
                                    <p style={{
                                        margin: '8px 0 0 0',
                                        fontSize: '12px',
                                        color: '#dc2626',
                                    }}>
                                        画像を読み込めませんでした。URLをご確認ください。
                                    </p>
                                )}
                            </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px'
                            }}>
                                違反内容:
                            </label>
                            <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                placeholder={
                                    annotations.find(a => a.id === editingAnnotationId)?.scope === 'off_capture'
                                        ? '手動でキャプチャした違反の指摘文言を入力...'
                                        : '画像上に表示されている指摘文言を入力...'
                                }
                                autoFocus={annotations.find(a => a.id === editingAnnotationId)?.scope !== 'off_capture'}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    resize: 'none',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    backgroundColor: (annotations.find(a => a.id === editingAnnotationId)?.scope === 'overall_page') ? '#f9fafb' : 'white',
                                    opacity: (annotations.find(a => a.id === editingAnnotationId)?.scope === 'overall_page') ? 0.7 : 1
                                }}
                                rows={4}
                                disabled={annotations.find(a => a.id === editingAnnotationId)?.scope === 'overall_page'}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px'
                            }}>
                                修正指摘内容:
                            </label>
                            <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                placeholder="レビュアー向けの補足や指示を入力..."
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    resize: 'none',
                                    fontSize: '14px',
                                    fontFamily: 'inherit'
                                }}
                                rows={3}
                            />
                        </div>

                        {/* Category Selection */}
                        <div style={{ marginTop: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px'
                            }}>
                                カテゴリ:
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                    color: '#374151'
                                }}
                            >
                                <option value="">カテゴリを選択</option>
                                {VIOLATION_CATEGORIES.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {enableUnfixableClassification && (() => {
                            const editingViolation = annotations.find(
                                (a) => a.id === editingAnnotationId
                            );
                            if (!editingViolation) return null;
                            return (
                                <ViolationFixabilityControls
                                    violation={editingViolation}
                                    mediaRuleDescription={pageUnfixableRuleDescription}
                                    onFixabilityChange={(fixability) =>
                                        handleFixabilityChange(editingViolation.id, fixability)
                                    }
                                    onUnfixableReasonPartChange={(part) =>
                                        handleUnfixableReasonPartChange(editingViolation.id, part)
                                    }
                                />
                            );
                        })()}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            marginTop: '16px'
                        }}>
                            <button
                                onClick={handleTextEditComplete}
                                style={{
                                    padding: '12px 20px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                保存（Shift+Enter）
                            </button>
                            <button
                                onClick={handleTextEditCancel}
                                style={{
                                    padding: '12px 20px',
                                    backgroundColor: 'transparent',
                                    color: '#6b7280',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease',
                                    background: 'white'
                                }}
                            >
                                キャンセル
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViolationCanvas;
